"use client"

import { useState, useContext, useEffect, useMemo, useRef, useCallback } from "react"
import { useLocation } from "react-router-dom"
import { ErpContext } from "../../context/ErpContext.js"
import { DataGrid, GridToolbar } from "@mui/x-data-grid"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Button,
  Card,
  CardContent,
  Chip,
  alpha,
  Tooltip,
  Modal,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Badge,
  Autocomplete,
  Avatar,
  Divider,
  Alert,
  CircularProgress,
  Collapse,
  useMediaQuery,
  Checkbox,
  FormControlLabel,
} from "@mui/material"
import { ThemeProvider, createTheme } from "@mui/material/styles"
import GroupsIcon from "@mui/icons-material/Groups"
import BallotIcon from "@mui/icons-material/Ballot"
import SaveIcon from "@mui/icons-material/Save"
import AddIcon from "@mui/icons-material/Add"
import ScaleIcon from "@mui/icons-material/Scale"
import VisibilityIcon from "@mui/icons-material/Visibility"
import EditIcon from "@mui/icons-material/Edit"
import CloseIcon from "@mui/icons-material/Close"
import SearchIcon from "@mui/icons-material/Search"
import FilterListIcon from "@mui/icons-material/FilterList"
import ClearIcon from "@mui/icons-material/Clear"
import CalendarTodayIcon from "@mui/icons-material/CalendarToday"
import AttachMoneyIcon from "@mui/icons-material/AttachMoney"
import TrendingUpIcon from "@mui/icons-material/TrendingUp"
import PercentIcon from "@mui/icons-material/Percent"
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn"
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet"
import LocationOnIcon from "@mui/icons-material/LocationOn"
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf"
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank"
import CheckBoxIcon from "@mui/icons-material/CheckBox"
import GridOnIcon from "@mui/icons-material/GridOn"
import PersonIcon from "@mui/icons-material/Person"
import PrintIcon from "@mui/icons-material/Print"
import DeleteIcon from "@mui/icons-material/Delete"

import dayjs from "dayjs"
import * as XLSX from "xlsx"
import Swal from "sweetalert2"
import jsPDF from "jspdf"
import "jspdf-autotable"
import CustomPagination from "../transaccionComponent/CustomPagination.js"
import "./TablaReceta.css"
import RecetaComponent from "./RecetaComponent.js"
import GestionGenerarClientesPaquete from "../transaccionComponent/GestionGenerarClientesPaquete.js"
import { generarPdfClientesReceta } from "../../context/pdf_esqueletos/recetaClientesPdf.js"
import { deliverPdf, reservePdfTarget } from "../../context/pdf_esqueletos/helpers/pdfDelivery.js"
import {
  getVendedoresTexto,
  hasVendedoresBomData,
  mergeVendedoresFromFin,
} from "../../context/pdf_esqueletos/helpers/vendedoresPdf.js"
import { generarPDFCuponera } from "../../utils/cuponeraUtils.js"
import {
  resolvePersonaParts,
  sortPersonasByApellidoNombre,
} from "../../utils/personaDisplay.js"

// Tema personalizado
const theme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#5b5b5f",
    },
    secondary: {
      main: "#f4b10e",
    },
    success: {
      main: "#4CAF50",
    },
    error: {
      main: "#F44336",
    },
    warning: {
      main: "#FF8042",
    },
    info: {
      main: "#0088FE",
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h6: {
      fontWeight: 600,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: "none",
          boxShadow: "none",
          "&:hover": {
            boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
          overflow: "visible",
        },
      },
    },
  },
})

/** Visibilidad inicial de columnas opcionales; monto/acciones se ajustan con permisos en el modelo efectivo. */
const DEFAULT_RECETA_COLUMN_VISIBILITY = {
  id: false,
  fechaInicio: false,
  fechaFin: false,
  cerrado: false,
  noches: false,
  precioPorPasajeros: false,
  costoTotalServicios: false,
  ganancia: false,
  porcentajeGanancia: false,
  fechaActualizacion: false,
  fechaCreacion: false,
}

const TablaReceta = () => {
  const location = useLocation()
  const {
    isNavbarCollapsed,
    getItemTresLetras,
    arrayItemTresLetras,
    itemTresLetras,
    itemAtributoNoEliminados,
    getItemAtributoNoEliminados,
    crearRecetaApi,
    eliminarBomApi,
    eliminarGrupoApi,
    getRecetasViajes,
    getBomByIdItem,
    getEntidadFilterTipoEntidad,
    getUbicacion,
    ubicaciones,
    getItemUbicacionFilterByUbicacion,
    getFechaHoraActualClient,
    getItemUbicacionStatsBatch,
    getParametrosGlobales,
    getTablaRecetaDatos,
    getTablaRecetaTotales,
    getTablaRecetaFinancieros,
    getClientesPorGrupoYDestino,
    getCuentaCorrienteCuotas,
    setCerradoPaqueteApi,
    generarPdfRecetas,
    user,
    tieneAccesoFuncionalidad,
  } = useContext(ErpContext)

  // Permisos granulares (INVENTARIO5 - Grupos de Viaje / TablaReceta)
  const mostrarPrecioOriginalFooter = tieneAccesoFuncionalidad(user, "INVENTARIO5", "INVENTARIO5_verPrecioOriginalFooter")
  const mostrarColumnaPrecio = tieneAccesoFuncionalidad(user, "INVENTARIO5", "INVENTARIO5_columnaPrecio")
  const mostrarBtnEditarGrupo = tieneAccesoFuncionalidad(user, "INVENTARIO5", "INVENTARIO5_btnEditarGrupo")
  const mostrarBtnEditarPasajeros = tieneAccesoFuncionalidad(user, "INVENTARIO5", "INVENTARIO5_btnEditarPasajeros")
  const mostrarBtnDescargarPdf = tieneAccesoFuncionalidad(user, "INVENTARIO5", "INVENTARIO5_btnDescargarPdfPasajeros")
  const mostrarBtnCuponera = tieneAccesoFuncionalidad(user, "INVENTARIO5", "INVENTARIO5_btnImprimirCuponera")
  const mostrarBtnExcel = tieneAccesoFuncionalidad(user, "INVENTARIO5", "INVENTARIO5_btnExportarExcel")
  const mostrarBtnExportarTablaPdf = tieneAccesoFuncionalidad(user, "INVENTARIO5", "INVENTARIO5_btnExportarTablaPdf")
  const [usaCuponera, setUsaCuponera] = useState(true)
  const tieneAlgunaAccion =
    mostrarBtnEditarGrupo ||
    mostrarBtnEditarPasajeros ||
    mostrarBtnDescargarPdf ||
    mostrarBtnExcel ||
    (mostrarBtnCuponera && usaCuponera)
  const [recetas, setRecetas] = useState([])
  const [loading, setLoading] = useState(false)
  const [openModal, setOpenModal] = useState(false)
  const [selectedReceta, setSelectedReceta] = useState(null)
  const [editedIngredientes, setEditedIngredientes] = useState([])
  const [preciosCostos, setPreciosCostos] = useState({})
  const [precioPaquete, setPrecioPaquete] = useState(0)
  const [cargandoDatosModal, setCargandoDatosModal] = useState(false)
  
  // Estados para modal de pasajeros
  const [openModalPasajeros, setOpenModalPasajeros] = useState(false)
  const [recetaPasajeros, setRecetaPasajeros] = useState(null)
  const gestionPasajerosRef = useRef(null) // Ref para acceder a funciones del componente hijo

  const today = dayjs()
  // ?? ELIMINADO: Filtros de fecha inicio/fin - ya no se usan para filtrar
  // Las fechas inicio/fin se siguen mostrando en la tabla pero no filtran
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(100)
  const [totalPages, setTotalPages] = useState(0)
  const [totalCount, setTotalCount] = useState(0)
  const [montoTotalTransacciones, setMontoTotalTransacciones] = useState(0)
  const DEFAULT_SORT = [{ field: "fechaViaje", sort: "desc" }]
  const [sortModel, setSortModel] = useState(DEFAULT_SORT)
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(() => ({ ...DEFAULT_RECETA_COLUMN_VISIBILITY }))
  const sortModelRef = useRef(DEFAULT_SORT)
  const paquetesFlagFetchedRef = useRef(false)
  const paquetesFlagValueRef = useRef(null)
  const paquetesFlagPromiseRef = useRef(null)
  const nochesAutoFlagFetchedRef = useRef(false)
  const nochesAutoFlagValueRef = useRef(null)
  const nochesAutoFlagPromiseRef = useRef(null)
  const hasInitializedRef = useRef(false)
  // Estados para totales de todas las páginas
  const [totalPrecioOriginal, setTotalPrecioOriginal] = useState(0)
  const [totalPrecioTotal, setTotalPrecioTotal] = useState(0)
  const [totalCostoTotal, setTotalCostoTotal] = useState(0)
  const [totalGanancia, setTotalGanancia] = useState(0)
  const [totalPasajerosConfirmados, setTotalPasajerosConfirmados] = useState(0)
  const [loadingTotales, setLoadingTotales] = useState(false)
  const totalesReqIdRef = useRef(0)

  const fetchLogoTenant = useCallback(async () => {
    try {
      const params = await getParametrosGlobales(["logoNavbarTenant"])
      return params?.logoNavbarTenant || null
    } catch (error) {
      console.error("Error cargando logo del tenant:", error)
      return null
    }
  }, [getParametrosGlobales])

  const fetchPdfConfig = useCallback(async () => {
    try {
      const params = await getParametrosGlobales([
        "logoNavbarTenant",
        "verFechaSalidaYFechaFinPdf",
        "verFechaSalidaPdf",
      ])
      const fechaViajePdfConfig =
        params?.verFechaSalidaYFechaFinPdf ?? params?.verFechaSalidaPdf
      return {
        logoTenant: params?.logoNavbarTenant || null,
        showFechaSalida:
          fechaViajePdfConfig === 1 ||
          fechaViajePdfConfig === "1" ||
          fechaViajePdfConfig === true ||
          fechaViajePdfConfig === "true",
      }
    } catch (error) {
      console.error("Error cargando configuración de PDFs:", error)
      return {
        logoTenant: null,
        showFechaSalida: true,
      }
    }
  }, [getParametrosGlobales])

  const cargarConfiguracionCuponera = useCallback(async () => {
    try {
      const params = await getParametrosGlobales(["usaCuponera"])
      const habilitada =
        params?.usaCuponera === "1" ||
        params?.usaCuponera === 1 ||
        params?.usaCuponera === "true" ||
        params?.usaCuponera === true
      setUsaCuponera(Boolean(habilitada))
    } catch (error) {
      console.error("Error cargando configuracion de cuponera:", error)
      setUsaCuponera(true)
    }
  }, [getParametrosGlobales])

  // Estados para filtros
  const [searchTextReceta, setSearchTextReceta] = useState("")
  const [filtroProducto, setFiltroProducto] = useState("")
  const [filtroDestino, setFiltroDestino] = useState("")
  const [filtroVendedor, setFiltroVendedor] = useState("")

  const [recetaSelected, setRecetaSelected] = useState(null)
  const [destinoSelected, setDestinoSelected] = useState(null)
  
  // ?? NUEVO: Filtros por fecha de viaje (fechaInicio del paquete)
  // Inicializar con el año actual: 1/1 a 31/12
  const [fechaViajeDesde, setFechaViajeDesde] = useState(today.startOf("year"))
  const [fechaViajeHasta, setFechaViajeHasta] = useState(today.endOf("year"))
  // ?? NUEVO: Filtro por fecha de inicio exacta
  const [fechaInicioSelected, setFechaInicioSelected] = useState(null)

  // ?? NUEVO: Estados para filtros de estado (cerrado/abierto)

  const [filtroCerrado, setFiltroCerrado] = useState(false)
  const [filtroAbierto, setFiltroAbierto] = useState(false)
  const [filtroCancelado, setFiltroCancelado] = useState(false)

  // Estado para tracking de cambios pendientes en estado de paquetes
  const [cambiosPendientes, setCambiosPendientes] = useState(new Map())
  // Map<idPaquete, { estadoOriginal: boolean, estadoNuevo: boolean, ...datosDelPaquete }>

  // Estado para modal de servicios
  const [modalServicios, setModalServicios] = useState({
    open: false,
    receta: null,
    servicios: []
  })
  const [modalVendedores, setModalVendedores] = useState({
    open: false,
    receta: null,
    vendedores: [],
    loading: false,
  })
  const [modalCuponera, setModalCuponera] = useState({
    open: false,
    receta: null,
    pasajeros: [],
    selectedKeys: [],
    loading: false,
    error: null,
    procesando: false,
  })
    // Estado para modal de pasajeros confirmados (lazy load)
    const [modalPasajerosConfirmados, setModalPasajerosConfirmados] = useState({
      open: false,
      receta: null,
      pasajeros: [],
      loading: false,
      error: null,
    })
  const [paquetesOcultarCantidadFlag, setPaquetesOcultarCantidadFlag] = useState(null)
  const [nochesCalculoAutomaticoFlag, setNochesCalculoAutomaticoFlag] = useState(null)
  const [modalEdicionRequiresRefetch, setModalEdicionRequiresRefetch] = useState(false)

  // Estado para modal de edición
  const [modalEdicion, setModalEdicion] = useState({
    open: false,
    paquete: null,
    ocultarCantidadYCostoXCantidad: null,
  })

  // Estado para modal de crear paquete
  const [openCrearPaqueteModal, setOpenCrearPaqueteModal] = useState(false)
  const [crearPaqueteModalConfig, setCrearPaqueteModalConfig] = useState({
    nochesCalculoAutomatico: null,
    resolved: false,
  })
  const [grupoSeleccionadoCrear, setGrupoSeleccionadoCrear] = useState(null) // ? Para mostrar el grupo seleccionado en el header

  // Estado para controlar visibilidad de filtros
  const isLaptopDown = useMediaQuery(theme.breakpoints.down("lg"))
  const [filtersOpen, setFiltersOpen] = useState(!isLaptopDown)
  
  // Ajustar filtersOpen cuando cambia el tamaño de pantalla
  useEffect(() => {
    setFiltersOpen(!isLaptopDown)
  }, [isLaptopDown])

  const ensurePaquetesOcultarCantidadFlag = useCallback(async () => {
    if (paquetesFlagFetchedRef.current) return paquetesFlagValueRef.current
    if (paquetesFlagPromiseRef.current) return paquetesFlagPromiseRef.current

    if (typeof getParametrosGlobales !== "function") {
      paquetesFlagFetchedRef.current = true
      paquetesFlagValueRef.current = false
      setPaquetesOcultarCantidadFlag(false)
      return false
    }
    paquetesFlagPromiseRef.current = (async () => {
      try {
        const res = await getParametrosGlobales(["paquetesOcultarCantidadYCostoXCantidad"])
        const raw = res?.paquetesOcultarCantidadYCostoXCantidad
        const parsed = raw === true || raw === 1 || raw === "1" || raw === "true" || raw === "TRUE"
        const value = Boolean(parsed)
        paquetesFlagFetchedRef.current = true
        paquetesFlagValueRef.current = value
        setPaquetesOcultarCantidadFlag(value)
        return value
      } catch (error) {
        console.error("Error obteniendo parámetro paquetesOcultarCantidadYCostoXCantidad:", error)
        paquetesFlagFetchedRef.current = true
        paquetesFlagValueRef.current = false
        setPaquetesOcultarCantidadFlag(false)
        return false
      } finally {
        paquetesFlagPromiseRef.current = null
      }
    })()

    return paquetesFlagPromiseRef.current
  }, [getParametrosGlobales])

  const ensureNochesCalculoAutomaticoFlag = useCallback(async () => {
    if (nochesAutoFlagFetchedRef.current) return nochesAutoFlagValueRef.current
    if (nochesAutoFlagPromiseRef.current) return nochesAutoFlagPromiseRef.current

    if (typeof getParametrosGlobales !== "function") {
      nochesAutoFlagFetchedRef.current = true
      nochesAutoFlagValueRef.current = false
      setNochesCalculoAutomaticoFlag(false)
      return false
    }
    nochesAutoFlagPromiseRef.current = (async () => {
      try {
        const res = await getParametrosGlobales(["nochesCalculoAutomatico"])
        const raw = res?.nochesCalculoAutomatico
        const parsed = raw === true || raw === 1 || raw === "1" || raw === "true" || raw === "TRUE"
        const value = Boolean(parsed)
        nochesAutoFlagFetchedRef.current = true
        nochesAutoFlagValueRef.current = value
        setNochesCalculoAutomaticoFlag(value)
        return value
      } catch (error) {
        console.error("Error obteniendo parámetro nochesCalculoAutomatico:", error)
        nochesAutoFlagFetchedRef.current = true
        nochesAutoFlagValueRef.current = false
        setNochesCalculoAutomaticoFlag(false)
        return false
      } finally {
        nochesAutoFlagPromiseRef.current = null
      }
    })()

    return nochesAutoFlagPromiseRef.current
  }, [getParametrosGlobales])

  useEffect(() => {
    ensurePaquetesOcultarCantidadFlag()
    ensureNochesCalculoAutomaticoFlag()
    cargarConfiguracionCuponera()
  // eslint-disable-next-line react-hooks/exhaustive-deps -- solo al montar
  }, [])

  // ?? ELIMINADO: Función para limpiar fechas de inicio (ya no se usa)
  // ?? ELIMINADO: Función buscarFechaInicioOptima (ya no se usa)

  // Efecto para búsqueda de ítems
  useEffect(() => {
    if (searchTextReceta.length >= 3) {
      arrayItemTresLetras([])
      getItemTresLetras(searchTextReceta, true)
    }
  }, [searchTextReceta])

  // ?? ELIMINADO: Efecto para actualizar endDate (ya no se usa)

  // ? OPTIMIZADO: Función para generar etiqueta de ítem con useCallback
  const generateRecetaLabel = useCallback((item) => {
    const activeAttributes = itemAtributoNoEliminados.filter((attr) => !attr.eliminado)

    const attributeValues = activeAttributes
      .map((attr, index) => {
        const attributeValue = item[`itemDatoAtributo${index + 1}`]
        return attributeValue ? attributeValue : ""
      })
      .join(" ")

    return `${item.codigo} - ${item.descripcion} ${attributeValues} ${item.codigoScanner}`
  }, [itemAtributoNoEliminados])

  const getNombreVendedor = useCallback((vendedorValue) => {
    if (!vendedorValue) return "-"
    if (typeof vendedorValue === "string") return vendedorValue

    if (typeof vendedorValue === "object") {
      const nombre = [vendedorValue.descripcion, vendedorValue.apellido].filter(Boolean).join(" ").trim()
      if (nombre) return nombre
      return vendedorValue.nombre || vendedorValue.razonSocial || vendedorValue.alias || "-"
    }

    return "-"
  }, [])

  const normalizarVendedores = useCallback((receta) => {
    const source = receta?.vendedores ?? receta?.vendedor
    if (!source) return []

    const asArray = Array.isArray(source) ? source : [source]

    return asArray
      .map((vendedor, index) => ({
        ...vendedor,
        _key: vendedor?.id ?? `vendedor-${index}`,
        nombre: getNombreVendedor(vendedor),
      }))
      .filter((vendedor) => vendedor.nombre && vendedor.nombre !== "-")
  }, [getNombreVendedor])

  const filteredRecetas = useMemo(() => {
    const termino = filtroVendedor.trim().toLowerCase()
    if (!termino) return recetas
    return recetas.filter((receta) => getNombreVendedor(receta.vendedor).toLowerCase().includes(termino))
  }, [recetas, filtroVendedor, getNombreVendedor])
    const CLIENT_COMPUTED_SORT_FIELDS = useMemo(
    () => new Set([
      "precioOriginal",
      "precioPorPasajeros",
      "costoTotalServicios",
      "ganancia",
      "porcentajeGanancia",
      "totalIngredientes",
      "pasajeros",
      "vendedor",
    ]),
    []
  )

  const compareTextForSort = useCallback((aValue, bValue) => {
    const aText = String(aValue ?? "").trim()
    const bText = String(bValue ?? "").trim()
    return aText.localeCompare(bText, "es", { sensitivity: "base", numeric: true })
  }, [])

  const compareRowIdentityForSort = useCallback((rowA, rowB) => {
    const idA = String(rowA?.id ?? rowA?.idPaquete ?? "")
    const idB = String(rowB?.id ?? rowB?.idPaquete ?? "")
    return compareTextForSort(idA, idB)
  }, [compareTextForSort])

  const getVendedorSortMeta = useCallback((row) => {
    const cantidadVendedores = Number(row?.cantidadVendedoresBom ?? 0)

    if (cantidadVendedores <= 0) {
      return { tier: 0, count: 0, label: "" }
    }

    if (cantidadVendedores === 1) {
      return {
        tier: 1,
        count: 1,
        label: String(row?.primerVendedorBom ?? "").trim(),
      }
    }

    const vendedoresOrdenados = Array.isArray(row?.vendedoresBomNombres)
      ? row.vendedoresBomNombres
          .map((nombre) => String(nombre ?? "").trim())
          .filter(Boolean)
          .sort(compareTextForSort)
      : []

    return {
      tier: 2,
      count: cantidadVendedores,
      label: vendedoresOrdenados.join(" | "),
    }
  }, [compareTextForSort])

  const sortRecetasByComputedField = useCallback((rowsToSort, field, direction) => {
    const multiplier = direction === "asc" ? 1 : -1

    return [...(Array.isArray(rowsToSort) ? rowsToSort : [])].sort((rowA, rowB) => {
      let result = 0

      if (field === "vendedor") {
        const vendedorA = getVendedorSortMeta(rowA)
        const vendedorB = getVendedorSortMeta(rowB)

        result = vendedorA.tier - vendedorB.tier

        if (result === 0 && vendedorA.tier === 1) {
          result = compareTextForSort(vendedorA.label, vendedorB.label)
        }

        if (result === 0 && vendedorA.tier === 2) {
          result = vendedorA.count - vendedorB.count
          if (result === 0) {
            result = compareTextForSort(vendedorA.label, vendedorB.label)
          }
        }
      } else {
        const valueA = Number(rowA?.[field] ?? 0)
        const valueB = Number(rowB?.[field] ?? 0)
        const safeValueA = Number.isFinite(valueA) ? valueA : 0
        const safeValueB = Number.isFinite(valueB) ? valueB : 0
        result = safeValueA - safeValueB
      }

      if (result === 0) {
        result = compareRowIdentityForSort(rowA, rowB)
      }

      return result * multiplier
    })
  }, [compareRowIdentityForSort, compareTextForSort, getVendedorSortMeta])


  const normalizarPasajeroCuponera = useCallback((pasajero, receta) => {
    const idEntidad = Number(pasajero?.idEntidad)
    const idItem = Number(receta?.idProductoTerminado)
    const idUbicacion = Number(receta?.destinoId ?? receta?.destino)
    const nombre = pasajero?.nombre || pasajero?.descripcion || ""
    const apellido = pasajero?.apellido || ""
    const dni = pasajero?.dniCuitCuil || pasajero?.dni || ""
    const key = `${idEntidad}-${idItem}-${idUbicacion}`

    return {
      key,
      idEntidad,
      idItem,
      idUbicacion,
      nombre,
      apellido,
      dniCuitCuil: dni,
      observaciones: pasajero?.observaciones || "",
      grupo: receta?.descripcionProductoTerminado || receta?.codigoProductoTerminado || `Grupo ${idItem}`,
      destino: receta?.destino || `Destino ${idUbicacion}`,
    }
  }, [])

  const cargarPasajerosCuponera = useCallback(async (recetaBase) => {
    const receta = recetaBase || null
    const idItem = Number(receta?.idProductoTerminado)
    const idUbicacion = Number(receta?.destinoId ?? receta?.destino)
    if (!receta || !Number.isFinite(idItem) || !Number.isFinite(idUbicacion)) {
      await Swal.fire({
        icon: "info",
        title: "Datos incompletos",
        text: "No se pudo identificar el grupo o destino para generar la cuponera.",
      })
      return
    }

    setModalCuponera({
      open: true,
      receta,
      pasajeros: [],
      selectedKeys: [],
      loading: true,
      error: null,
      procesando: false,
    })

    try {
      const pasajeros = await getClientesPorGrupoYDestino(receta.idProductoTerminado, receta.destinoId ?? receta.destino)
      const pasajerosOrdenados = (Array.isArray(pasajeros) ? pasajeros : [])
        .map((pasajero) => normalizarPasajeroCuponera(pasajero, receta))
        .filter(
          (pasajero) =>
            Number.isFinite(pasajero.idEntidad) &&
            Number.isFinite(pasajero.idItem) &&
            Number.isFinite(pasajero.idUbicacion)
        )
        .sort((a, b) => {
        const grupoComp = String(a.grupo || "").localeCompare(String(b.grupo || ""), "es")
        if (grupoComp !== 0) return grupoComp
        const destinoComp = String(a.destino || "").localeCompare(String(b.destino || ""), "es")
        if (destinoComp !== 0) return destinoComp
        const apellidoComp = String(a.apellido || "").localeCompare(String(b.apellido || ""), "es")
        if (apellidoComp !== 0) return apellidoComp
        return String(a.nombre || "").localeCompare(String(b.nombre || ""), "es")
      })

      setModalCuponera((prev) => ({
        ...prev,
        pasajeros: pasajerosOrdenados,
        selectedKeys: pasajerosOrdenados.map((p) => p.key),
        loading: false,
      }))
    } catch (error) {
      console.error("Error cargando pasajeros para cuponera:", error)
      setModalCuponera((prev) => ({
        ...prev,
        loading: false,
        error: "No se pudieron cargar los pasajeros para cuponera.",
      }))
    }
  }, [getClientesPorGrupoYDestino, normalizarPasajeroCuponera])

  const handleOpenModalCuponeraDesdeFila = useCallback(async (receta) => {
    if (!usaCuponera) {
      await Swal.fire({
        icon: "info",
        title: "Cuponera desactivada",
        text: "La cuponera esta desactivada en parametros globales.",
      })
      return
    }
    await cargarPasajerosCuponera(receta)
  }, [cargarPasajerosCuponera, usaCuponera])

  const handleCloseModalCuponera = useCallback(() => {
    setModalCuponera({
      open: false,
      receta: null,
      pasajeros: [],
      selectedKeys: [],
      loading: false,
      error: null,
      procesando: false,
    })
  }, [])

  const handleTogglePasajeroCuponera = useCallback((key) => {
    setModalCuponera((prev) => {
      const selectedSet = new Set(prev.selectedKeys)
      if (selectedSet.has(key)) selectedSet.delete(key)
      else selectedSet.add(key)
      return { ...prev, selectedKeys: Array.from(selectedSet) }
    })
  }, [])

  const handleSeleccionarTodosCuponera = useCallback(() => {
    setModalCuponera((prev) => ({
      ...prev,
      selectedKeys: prev.pasajeros.map((p) => p.key),
    }))
  }, [])

  const handleLimpiarSeleccionCuponera = useCallback(() => {
    setModalCuponera((prev) => ({ ...prev, selectedKeys: [] }))
  }, [])

  const handleImprimirCuponeraSeleccionados = useCallback(async () => {
    const seleccion = modalCuponera.pasajeros.filter((p) => modalCuponera.selectedKeys.includes(p.key))
    if (!seleccion.length) {
      await Swal.fire({
        icon: "info",
        title: "Sin pasajeros seleccionados",
        text: "Seleccione al menos un pasajero para imprimir cuponera.",
      })
      return
    }

    setModalCuponera((prev) => ({ ...prev, procesando: true }))
    try {
      const { logoTenant, showFechaSalida } = await fetchPdfConfig()
      let generadas = 0
      const omitidas = []

      for (const pasajero of seleccion) {
        try {
          const cuotasInfo = await getCuentaCorrienteCuotas(pasajero.idEntidad, pasajero.idItem, pasajero.idUbicacion)
          const cuotas = Array.isArray(cuotasInfo?.cuotas) ? cuotasInfo.cuotas : []

          if (!cuotas.length || cuotasInfo?.liberado === true) {
            omitidas.push(`${pasajero.nombre} ${pasajero.apellido}`.trim() || `Entidad ${pasajero.idEntidad}`)
            continue
          }

          const paqueteInfo = {
            item: cuotasInfo?.item || { id: pasajero.idItem, descripcion: pasajero.grupo },
            ubicacion: cuotasInfo?.ubicacion || { id: pasajero.idUbicacion, descripcion: pasajero.destino },
            fechaInicio: cuotasInfo?.paquete?.fechaInicio || cuotasInfo?.fechaInicio || null,
          }

          const clienteData = {
            id: pasajero.idEntidad,
            nombre: pasajero.nombre || "Pasajero",
            apellido: pasajero.apellido || "",
          }

          await generarPDFCuponera({
            paqueteInfo,
            cuotas,
            clienteData,
            logoBase64: logoTenant,
            showFechaSalida,
          })
          generadas += 1
        } catch (error) {
          console.error("Error generando cuponera de pasajero:", pasajero, error)
          omitidas.push(`${pasajero.nombre} ${pasajero.apellido}`.trim() || `Entidad ${pasajero.idEntidad}`)
        }
      }

      setModalCuponera((prev) => ({ ...prev, procesando: false }))

      if (generadas > 0 && omitidas.length === 0) {
        await Swal.fire({
          icon: "success",
          title: "Cuponeras generadas",
          text: `Se generaron ${generadas} cuponera(s) correctamente.`,
        })
      } else if (generadas > 0 && omitidas.length > 0) {
        await Swal.fire({
          icon: "warning",
          title: "Proceso finalizado con omisiones",
          text: `Generadas: ${generadas}. Omitidas: ${omitidas.length}.`,
        })
      } else {
        await Swal.fire({
          icon: "info",
          title: "No se genero ninguna cuponera",
          text: "Los pasajeros seleccionados no tienen cuotas disponibles para imprimir.",
        })
      }
    } catch (error) {
      console.error("Error en impresion masiva de cuponeras:", error)
      setModalCuponera((prev) => ({ ...prev, procesando: false }))
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudo generar la cuponera. Intente nuevamente.",
      })
    }
  }, [fetchPdfConfig, getCuentaCorrienteCuotas, modalCuponera.pasajeros, modalCuponera.selectedKeys])

  // Función para manejar cambio de ítem
  const handleRecetaChange = (event, newValue) => {
    setRecetaSelected(newValue)
    if (newValue) {
      const defaultValues = itemAtributoNoEliminados.map((attr, index) => ({
        value: newValue[`itemDatoAtributo${index + 1}`] || "",
      }))
    }
  }

  const handleDestinoChange = (event, newValue) => {
    setDestinoSelected(newValue)
  }

  // Funciones para modal de servicios (BOM bajo demanda si la fila no trae ingredientes)
  const handleOpenModalServicios = async (receta) => {
    let servicios = receta.ingredientes || []
    console.log('[handleOpenModalServicios] abierto receta.id:', receta?.id, 'idProductoTerminado:', receta?.idProductoTerminado, 'ingredientes inicial:', servicios?.length ?? 0)
    if (servicios.length === 0 && receta.idProductoTerminado) {
      try {
        const bom = await getBomByIdItem(receta.idProductoTerminado, receta.destinoId ?? receta.destino, receta.idPaquete ?? receta.id)
        servicios = Array.isArray(bom) ? bom : (bom?.servicios || bom?.data || [])
        console.log('[handleOpenModalServicios] BOM cargado, servicios.length:', servicios?.length ?? 0, 'con esVendedor:', servicios?.filter((s) => !!s?.esVendedor)?.length ?? 0)
      } catch (err) {
        console.error("Error cargando Servicios del grupo:", err)
      }
    }

    // Excluir servicios cuyo proveedor es vendedor (esVendedor=true); esos van al modal de vendedores.
    const antesFiltro = servicios.length
    servicios = servicios.filter((servicio) => !servicio?.esVendedor)
        console.log('[handleOpenModalServicios] después filtro !esVendedor: antes=', antesFiltro, 'después=', servicios.length)

    setModalServicios({
      open: true,
      receta: { ...receta, ingredientes: servicios },
      servicios,
    })
  }

  const handleCloseModalServicios = () => {
    setModalServicios({
      open: false,
      receta: null,
      servicios: []
    })
  }

  const handleOpenModalVendedores = useCallback(async (receta) => {
    console.log('[handleOpenModalVendedores] abierto receta:', { id: receta?.id, idPaquete: receta?.idPaquete, idProductoTerminado: receta?.idProductoTerminado, ingredientesLength: receta?.ingredientes?.length ?? 0 })
    // Abrir modal de inmediato con loading (permite ver vendedores del BOM aunque el badge sea 0)
    setModalVendedores({
      open: true,
      receta,
      vendedores: [],
      loading: true,
    })

    // Cargar BOM para obtener proveedores con esVendedor=true
    let servicios = receta.ingredientes || []
    if (servicios.length === 0 && receta.idProductoTerminado) {
      try {
        const bom = await getBomByIdItem(receta.idProductoTerminado, receta.destinoId ?? receta.destino, receta.idPaquete ?? receta.id)
        servicios = Array.isArray(bom) ? bom : (bom?.servicios || bom?.data || [])
        console.log('[handleOpenModalVendedores] BOM cargado servicios.length:', servicios?.length ?? 0, 'con esVendedor:', servicios?.filter((s) => !!s?.esVendedor)?.length ?? 0)
      } catch (err) {
        console.error("Error cargando servicios para vendedores:", err)
      }
    } else {
      console.log('[handleOpenModalVendedores] usando ingredientes de receta, servicios.length:', servicios?.length ?? 0, 'con esVendedor:', servicios?.filter((s) => !!s?.esVendedor)?.length ?? 0)
    }

    // Armar listado de servicios con proveedor (solo esVendedor=true)
    const serviciosConProveedor = (servicios || [])
      .filter((s) => !!s?.esVendedor)
      .map((s, index) => {
        const nombreServicio = s?.materiaPrima?.descripcion || s?.descripcion || "Sin servicio"
        const nombreProveedor = [s?.entidad?.descripcion, s?.entidad?.apellido]
          .filter(Boolean)
          .join(" ")
          .trim() || (s?.idEntidad ? `Proveedor ${s.idEntidad}` : "-")

        return {
          _key: `${s?.idMateriaPrima || "servicio"}-${s?.idEntidad || "proveedor"}-${index}`,
          nombreServicio,
          nombreProveedor,
        }
      })

    console.log('[handleOpenModalVendedores] serviciosConProveedor:', serviciosConProveedor?.length ?? 0, serviciosConProveedor)

    setModalVendedores({
      open: true,
      receta,
      vendedores: serviciosConProveedor,
      loading: false,
    })
  }, [getBomByIdItem])

  const handleCloseModalVendedores = useCallback(() => {
    setModalVendedores({
      open: false,
      receta: null,
      vendedores: [],
      loading: false,
    })
  }, [])

  const handleOpenModalPasajerosConfirmados = async (receta) => {
    setModalPasajerosConfirmados({
      open: true,
      receta,
      pasajeros: [],
      loading: true,
      error: null,
    })

    try {
      const pasajerosData = await getClientesPorGrupoYDestino(
        receta.idProductoTerminado,
        receta.destinoId
      )

      const pasajerosConfirmados = sortPersonasByApellidoNombre(
        (pasajerosData || []).filter((p) => p.viaja !== false)
      )

      setModalPasajerosConfirmados((prev) => ({
        ...prev,
        pasajeros: pasajerosConfirmados,
        loading: false,
      }))
    } catch (error) {
      console.error("Error cargando pasajeros confirmados:", error)
      setModalPasajerosConfirmados((prev) => ({
        ...prev,
        loading: false,
        error: "No se pudieron cargar los pasajeros.",
      }))
    }
  }

  const handleCloseModalPasajerosConfirmados = () => {
    setModalPasajerosConfirmados({
      open: false,
      receta: null,
      pasajeros: [],
      loading: false,
      error: null,
    })
  }

  // Funciones para modal de edición
  const handleOpenModalEdicion = async (receta) => {
    const ocultarFlagPromise = ensurePaquetesOcultarCantidadFlag()
    // Cargar el item completo si no está en itemTresLetras
    const itemExistente = itemTresLetras?.find(it => it.id === receta.idProductoTerminado)
    if (!itemExistente && receta.codigoProductoTerminado) {
      // Buscar el item por código para obtener los atributos
      await getItemTresLetras(receta.codigoProductoTerminado.substring(0, 3))
    }
    
    // Asegurar que los atributos están cargados
    if (!itemAtributoNoEliminados || itemAtributoNoEliminados.length === 0) {
      await getItemAtributoNoEliminados()
    }
    
    const ocultarFlag = await ocultarFlagPromise

    setModalEdicion({
      open: true,
      paquete: receta,
      ocultarCantidadYCostoXCantidad: ocultarFlag,
    })
    setModalEdicionRequiresRefetch(false)
  }

  const handleCloseModalEdicion = () => {
    setModalEdicion({
      open: false,
      paquete: null,
      ocultarCantidadYCostoXCantidad: null,
    })
    setModalEdicionRequiresRefetch(false)

    refetchTablaReceta()
  }

  const handleRecetaGuardada = () => {
    setModalEdicionRequiresRefetch(true)
    handleCloseModalEdicion()
  }

  const handleOpenCrearPaqueteModal = async () => {
    const [, nochesFlag] = await Promise.all([
      ensurePaquetesOcultarCantidadFlag(),
      ensureNochesCalculoAutomaticoFlag(),
    ])
    setCrearPaqueteModalConfig({
      nochesCalculoAutomatico: nochesFlag,
      resolved: true,
    })
    setOpenCrearPaqueteModal(true)
  }

  const handleCloseCrearPaqueteModal = () => {
    setOpenCrearPaqueteModal(false)
    setCrearPaqueteModalConfig({
      nochesCalculoAutomatico: null,
      resolved: false,
    })
    setGrupoSeleccionadoCrear(null) // ? Resetear el grupo seleccionado al cerrar
  }

  // Función para eliminar paquete
  const handleEliminarPaquete = async (receta) => {
    try {
      const result = await Swal.fire({
        title: '¿Estás seguro?',
        text: `¿Deseas eliminar el paquete "${receta.descripcionProductoTerminado}" del destino "${receta.destino}"?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Sí, eliminar',
        cancelButtonText: 'Cancelar',
      })

      if (result.isConfirmed) {
        setLoading(true)
        // Usar idPaquete si está disponible (más preciso), sino usar idProductoTerminado + destinoId
        await eliminarBomApi(receta.idProductoTerminado, receta.destinoId, receta.idPaquete || null)
        
        await Swal.fire({
          title: '¡Eliminado!',
          text: 'El paquete ha sido eliminado correctamente.',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false
        })

        // Recargar datos de la tabla
        const idReceta = recetaSelected ? recetaSelected.id : null
        const idDestino = destinoSelected ? destinoSelected.id : null
        let cerradoParam = null
        if (filtroCerrado) cerradoParam = true
        else if (filtroAbierto) cerradoParam = false
        const canceladoParam = filtroCancelado ? true : null
        fetchData(idReceta, null, idDestino, page, limit, fechaViajeDesde, fechaViajeHasta, fechaInicioSelected, cerradoParam, canceladoParam)
      }
    } catch (error) {
      console.error("Error al eliminar paquete:", error)
      await Swal.fire({
        title: 'Error',
        text: error.response?.data?.message || 'Error al eliminar el paquete',
        icon: 'error',
        confirmButtonText: 'Aceptar'
      })
    } finally {
      setLoading(false)
    }
  }

  // Función para eliminar grupo completo (soft delete, bloqueado si tiene cuotas pagadas)
  const handleEliminarGrupo = async (receta) => {
    const result = await Swal.fire({
      title: '¿Eliminar grupo?',
      text: `¿Deseas eliminar el grupo "${receta.descripcionProductoTerminado}"? Solo es posible si ningún pasajero tiene cuotas pagadas.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
    })

    if (!result.isConfirmed) return

    try {
      setLoading(true)
      await eliminarGrupoApi(receta.idProductoTerminado)
      await Swal.fire({
        title: '¡Eliminado!',
        text: 'El grupo fue eliminado correctamente.',
        icon: 'success',
        timer: 2000,
        showConfirmButton: false,
      })
      const idReceta = recetaSelected ? recetaSelected.id : null
      const idDestino = destinoSelected ? destinoSelected.id : null
      let cerradoParam = null
      if (filtroCerrado) cerradoParam = true
      else if (filtroAbierto) cerradoParam = false
      const canceladoParam = filtroCancelado ? true : null
      fetchData(idReceta, null, idDestino, page, limit, fechaViajeDesde, fechaViajeHasta, fechaInicioSelected, cerradoParam, canceladoParam)
    } catch (error) {
      await Swal.fire({
        title: 'No se puede eliminar',
        text: error.response?.data?.message || 'Error al eliminar el grupo',
        icon: 'error',
        confirmButtonText: 'Aceptar',
      })
    } finally {
      setLoading(false)
    }
  }

  const handlePaqueteCreado = () => {
    // Cerrar el modal
    setOpenCrearPaqueteModal(false)
    // Refrescar la tabla después de crear el paquete
    const idReceta = recetaSelected ? recetaSelected.id : null
    const idDestino = destinoSelected ? destinoSelected.id : null
    let cerradoParam = null
    if (filtroCerrado) cerradoParam = true
    else if (filtroAbierto) cerradoParam = false
    const canceladoParam = filtroCancelado ? true : null
    fetchData(idReceta, null, idDestino, page, limit, fechaViajeDesde, fechaViajeHasta, fechaInicioSelected, cerradoParam, canceladoParam)
  }

  // ?? FUNCIÓN PARA FORMATEAR MONEDA ARGENTINA
  const formatearMoneda = (valor) => {
    if (typeof valor !== 'number' || isNaN(valor)) return '$0'
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(valor)
  }

  // ? OPTIMIZADO: Generar atributos como subtexto con useCallback
  const generarAtributosSubtexto = useCallback((item) => {
    if (!item || !itemAtributoNoEliminados) return ""
    
    const activeAttributes = itemAtributoNoEliminados.filter((attr) => !attr.eliminado)
    const attributeValues = activeAttributes
      .map((attr, index) => {
        const attributeValue = item[`itemDatoAtributo${index + 1}`]
        return attributeValue ? attributeValue : ""
      })
      .filter((value) => value !== "" && value !== "undefined")
      .join(" y ")
    
    return attributeValues
  }, [itemAtributoNoEliminados])

  // ? OPTIMIZADO: Generar color gradiente con useCallback
  const generarColorGradiente = useCallback((porcentaje) => {
    // Normalizar el porcentaje entre 0 y 100
    const porcentajeNormalizado = Math.max(0, Math.min(100, porcentaje))
    
    // Definir colores del gradiente
    const colores = {
      muyBajo: '#ff4444',    // Rojo - Pérdida o muy baja ganancia
      bajo: '#ff8800',       // Naranja - Ganancia baja
      medio: '#ffcc00',      // Amarillo - Ganancia media
      bueno: '#88cc00',      // Verde claro - Buena ganancia
      excelente: '#44aa44'   // Verde - Excelente ganancia
    }
    
    // Definir rangos de porcentaje
    if (porcentajeNormalizado < 0) return colores.muyBajo
    if (porcentajeNormalizado < 10) return colores.bajo
    if (porcentajeNormalizado < 20) return colores.medio
    if (porcentajeNormalizado < 30) return colores.bueno
    return colores.excelente
  }, [])

  const fetchInitialData = async () => {
    try {
      await getItemAtributoNoEliminados()
    } catch (error) {}
  }

  // ? OPTIMIZADO: Crear caché compartido a nivel de componente para persistir entre renders
  const cacheMontos = useRef({})
  
  // ? OPTIMIZADO: Los datos financieros ya vienen calculados del backend usando BOM
  // No es necesario recalcular, solo retornar las recetas tal como vienen
  const cargarDatosFinancieros = useCallback(async (recetas) => {
    // El backend ya calculó precioPaquete, costoTotalServicios, ganancia, etc. usando BOM
    return recetas
  }, [])

  // ? OPTIMIZADO: Calcular totales con useCallback
  const calcularTotalesTodasLasPaginas = useCallback(async (idReceta, idMateriaPrima, idDestino, startDate, endDate, cerrado = null, reqId = null) => {
    try {
      const response = await getTablaRecetaTotales(
        startDate,
        endDate,
        idReceta,
        idMateriaPrima,
        idDestino,
        null,
        null,
        cerrado
      )

      const sumaPrecioOriginal = Number(response?.totalPrecioOriginal || 0)
      const sumaPrecioTotal = Number(response?.totalPrecioTotal || 0)
      const sumaCostoTotal = Number(response?.totalCostoTotal || 0)
      const sumaGanancia = Number(response?.totalGanancia || 0)
      const sumaPasajerosConfirmados = Number(response?.totalPasajerosConfirmados || 0)

      // Evitar que una respuesta vieja pise a una nueva
      if (reqId !== null && reqId !== totalesReqIdRef.current) return

      setTotalPrecioOriginal(sumaPrecioOriginal)
      setTotalPrecioTotal(sumaPrecioTotal)
      setTotalCostoTotal(sumaCostoTotal)
      setTotalGanancia(sumaGanancia)
      setTotalPasajerosConfirmados(sumaPasajerosConfirmados)
    } catch (error) {
      console.error("Error calculando totales:", error)
      if (reqId !== null && reqId !== totalesReqIdRef.current) return
      setTotalPrecioOriginal(0)
      setTotalPrecioTotal(0)
      setTotalCostoTotal(0)
      setTotalGanancia(0)
      setTotalPasajerosConfirmados(0)
    } finally {
      if (reqId !== null && reqId !== totalesReqIdRef.current) return
      setLoadingTotales(false)
    }
  }, [getTablaRecetaTotales])

  // Listado ligero: sin ingredientes ni financieros (se rellenan después con getTablaRecetaFinancieros)
  const transformarRecetasLigero = useCallback((recetasData) => {
    return (recetasData || []).map((receta) => {
      const idPaq = receta.idPaquete || receta.id
      return {
        id: receta.id,
        idPaquete: idPaq,
        descripcionProductoTerminado: receta.descripcionProductoTerminado || "Sin descripción",
        codigoProductoTerminado: receta.codigoProductoTerminado || "Sin código",
        idProductoTerminado: receta.idProductoTerminado,
        destino: (receta.ubicacion?.descripcion || receta.destino) ?? "Sin destino",
        destinoId: receta.destino ?? receta.destinoId,
        butacas: Number(receta.butacas ?? receta.inventario ?? 0) || 0,
        fechaInicio: receta.fechaInicio ? dayjs(receta.fechaInicio).format("DD/MM/YYYY") : "N/A",
        fechaFin: receta.fechaFin ? dayjs(receta.fechaFin).format("DD/MM/YYYY") : "N/A",
        observaciones: receta.observaciones || "",
        fechaInicioRaw: receta.fechaInicio ?? null,
        fechaFinRaw: receta.fechaFin ?? null,
        noches: receta.noches || 0,
        cerrado: receta.cerrado || false,
        idVendedor: receta.idVendedor ?? null,
        vendedor: receta.vendedor ?? null,
        vendedores: Array.isArray(receta.vendedores) ? receta.vendedores : [],
        totalIngredientes: 0,
        cantidadTotal: "0",
        ingredientes: [],
        nombresIngredientes: "",
        fechaCreacion: receta.createdAt ? dayjs(receta.createdAt).format("DD/MM/YYYY") : "N/A",
        fechaActualizacion: receta.updatedAt ? dayjs(receta.updatedAt).format("DD/MM/YYYY") : "N/A",
        horaCreacion: receta.createdAt ? dayjs(receta.createdAt).format("HH:mm") : "N/A",
        horaActualizacion: receta.updatedAt ? dayjs(receta.updatedAt).format("HH:mm") : "N/A",
        primerIngrediente: "-",
        codigoPrimerIngrediente: "",
        precioPaquete: Number(receta.monto ?? 0) || 0,
        monto: receta.monto ?? 0,
        costoTotalServicios: 0,
        ganancia: 0,
        porcentajeGanancia: 0,
        pasajeros: 0,
        precioOriginal: 0,
        precioPorPasajeros: 0,
      }
    })
  }, [])

    // ? OPTIMIZADO: fetchData con useCallback
    const fetchData = useCallback(async (
      idReceta,
      idMateriaPrima,
      idDestino,
      page,
      limit = 100,
      fechaViajeDesde = null,
      fechaViajeHasta = null,
      fechaInicio = null,
      cerrado = null,
      cancelado = null,
      sortFieldParam = null,
      sortOrderParam = null
    ) => {
    try {
      setLoading(true)

      
// ?? FECHAS DE VIAJE: Normalizar si existen (fechaInicio del paquete)
      let dateViajeDesde = null
      let dateViajeHasta = null

      if (fechaViajeDesde) {
        const formattedViajeDesde = fechaViajeDesde.format("YYYY-MM-DD")
        const resultViajeDesde = await getFechaHoraActualClient(formattedViajeDesde)
        dateViajeDesde = dayjs(resultViajeDesde.fechaServidor).startOf("day").toDate()
      }

      if (fechaViajeHasta) {
        const formattedViajeHasta = fechaViajeHasta.format("YYYY-MM-DD")
        const resultViajeHasta = await getFechaHoraActualClient(formattedViajeHasta)
        dateViajeHasta = dayjs(resultViajeHasta.fechaServidor).endOf("day").toDate()
      }

      // ?? FECHA DE INICIO: Normalizar si existe
      let dateInicio = null
      if (fechaInicio) {
        const formattedInicio = fechaInicio.format("YYYY-MM-DD")
        const resultInicio = await getFechaHoraActualClient(formattedInicio)
        // Para igualdad exacta, necesitamos rango del día completo
        dateInicio = dayjs(resultInicio.fechaServidor).startOf("day").toDate()
      }

       // Paso 1: listado ligero (sin BOM ni financieros)
       const activeSort = Array.isArray(sortModelRef.current) && sortModelRef.current.length ? sortModelRef.current[0] : DEFAULT_SORT[0]
       const sortField = sortFieldParam || activeSort?.field || "fechaViaje"
       const sortOrder = sortOrderParam || (activeSort?.sort === "asc" ? "asc" : "desc")
       const isClientComputedSort = CLIENT_COMPUTED_SORT_FIELDS.has(sortField)
       const backendSortField = isClientComputedSort ? "fechaViaje" : sortField
 
      const response = await getTablaRecetaDatos(
        dateViajeDesde,
        dateViajeHasta,
        idReceta,
        idMateriaPrima,
        idDestino,
        page,
        limit,
        null,
        null,
        dateInicio,
        cerrado,
        cancelado,
        true, // soloListado
        backendSortField,
        sortOrder
      )

      let recetasData = []
      if (response.data) {
        setTotalPages(response.totalPages || 0)
        setTotalCount(response.total || 0)
        recetasData = response.data
      } else {
        recetasData = response
      }
      const recetasTransformadas = transformarRecetasLigero(recetasData)
      let rowsToDisplay = recetasTransformadas
      let shouldReplaceRows = false

      setRecetas(recetasTransformadas)

      // Paso 2: financieros solo para los paquetes de la página actual
      const idPaquetes = recetasData.map((r) => r.id || r.idPaquete).filter(Boolean)
      if (idPaquetes.length > 0) {
        try {
          const { financieros } = await getTablaRecetaFinancieros(idPaquetes)
          console.log("[TablaReceta] financieros recibidos:", financieros)
          console.log("[TablaReceta] idPaquetes solicitados:", idPaquetes)
          rowsToDisplay = recetasTransformadas.map((r) => {
              const fin = financieros[r.id] || financieros[r.idPaquete]
              if (!fin) {
                console.log("[TablaReceta] merge financieros: sin fin para r.id=" + (r.id ?? r.idPaquete), { rId: r.id, rIdPaquete: r.idPaquete })
                return r
              }
              const merged = {
                ...r,
                pasajeros: Number(fin.pasajeros || 0),
                precioPaquete: Number(fin.precioPaquete || 0),
                monto: r.monto ?? fin.precioPaquete,
                costoTotalServicios: Number(fin.costoTotalServicios || 0),
                ganancia: Number(fin.ganancia || 0),
                porcentajeGanancia: Number(fin.porcentajeGanancia || 0),
                precioOriginal: Number(fin.precioOriginal || 0),
                precioPorPasajeros: Number(fin.precioPorPasajeros || 0),
                totalIngredientes: Number(fin.cantidadServicios ?? 0),
                cantidadVendedoresBom: Number(fin.cantidadVendedores ?? 0),
                primerVendedorBom: fin.primerVendedorBom ?? null,
                vendedoresBomNombres: Array.isArray(fin.vendedoresBomNombres) ? fin.vendedoresBomNombres : [],
              }
              if (merged.cantidadVendedoresBom > 0) {
                console.log("[TablaReceta] merge financieros: row merged", {
                  id: merged.id ?? merged.idPaquete,
                  cantidadVendedoresBom: merged.cantidadVendedoresBom,
                  primerVendedorBom: merged.primerVendedorBom,
                })
              }
              return merged
            })
          shouldReplaceRows = true
        } catch (err) {
          console.error("Error cargando financieros:", err)
        }
      }

      if (isClientComputedSort) {
        rowsToDisplay = sortRecetasByComputedField(rowsToDisplay, sortField, sortOrder)
        shouldReplaceRows = true
      }

      if (shouldReplaceRows) {
        setRecetas(rowsToDisplay)
      }

      // Calcular totales de todas las páginas (en background, no bloquea la UI)
      const reqId = ++totalesReqIdRef.current
      setLoadingTotales(true)
      calcularTotalesTodasLasPaginas(
        idReceta,
        idMateriaPrima,
        idDestino,
        dateViajeDesde,
        dateViajeHasta,
        cerrado,
        reqId
      )
    } catch (error) {
      console.error("Error fetching recetas:", error)
    } finally {
      setLoading(false)
    }
  }, [
    CLIENT_COMPUTED_SORT_FIELDS,
    getFechaHoraActualClient,
    getTablaRecetaDatos,
    getTablaRecetaFinancieros,
    transformarRecetasLigero,
    calcularTotalesTodasLasPaginas,
    sortRecetasByComputedField,
  ])

  const handleSortModelChange = (newModel) => {
    const current = Array.isArray(sortModelRef.current) && sortModelRef.current.length
      ? sortModelRef.current[0]
      : DEFAULT_SORT[0]
    const clickedField = Array.isArray(newModel) && newModel.length
      ? (newModel[0]?.field || current?.field || DEFAULT_SORT[0].field)
      : (current?.field || DEFAULT_SORT[0].field)
    const nextSort = Array.isArray(newModel) && newModel.length ? newModel[0]?.sort : "desc"
    const normalizedSort = nextSort === "asc" ? "asc" : "desc"
    const nextModel = [{ field: clickedField, sort: normalizedSort }]

    // Evitar bucle: comparar contra el state (no el ref) para no bloquear el primer click
    // cuando ref y state están desincronizados entre renders.
    const stateField = Array.isArray(sortModel) && sortModel.length ? sortModel[0]?.field : DEFAULT_SORT[0].field
    const stateSort = Array.isArray(sortModel) && sortModel.length
      ? (sortModel[0]?.sort === "asc" ? "asc" : "desc")
      : "desc"
    if (stateField === clickedField && stateSort === normalizedSort) return

    sortModelRef.current = nextModel
    setSortModel(nextModel)

    if (CLIENT_COMPUTED_SORT_FIELDS.has(clickedField)) {
      setRecetas((prevRows) => sortRecetasByComputedField(prevRows, clickedField, normalizedSort))
      return
    }

    const idReceta = recetaSelected ? recetaSelected.id : null
    const idDestino = destinoSelected ? destinoSelected.id : null
    let cerradoParam = null
    if (filtroCerrado) cerradoParam = true
    else if (filtroAbierto) cerradoParam = false
    const canceladoParam = filtroCancelado ? true : null

    setPage(1)
    fetchData(
      idReceta,
      null,
      idDestino,
      1,
      limit,
      fechaViajeDesde,
      fechaViajeHasta,
      fechaInicioSelected,
      cerradoParam,
      canceladoParam,
      clickedField,
      normalizedSort
    )
  }

  useEffect(() => {
    if (hasInitializedRef.current) return
    hasInitializedRef.current = true

    const inicializar = async () => {
      const today = dayjs()
      const fechaDesde = today.startOf("year")
      const fechaHasta = today.endOf("year")

      setFechaViajeDesde(fechaDesde)
      setFechaViajeHasta(fechaHasta)

      await getUbicacion()
      await getItemAtributoNoEliminados()

      const filtrosPaquete = location.state?.filtrosPaquete
      if (filtrosPaquete?.recetaSelected || filtrosPaquete?.destinoSelected || filtrosPaquete?.fechaInicioSelected) {
        if (filtrosPaquete.recetaSelected) setRecetaSelected(filtrosPaquete.recetaSelected)
        if (filtrosPaquete.destinoSelected) setDestinoSelected(filtrosPaquete.destinoSelected)
        if (filtrosPaquete.fechaInicioSelected) setFechaInicioSelected(dayjs(filtrosPaquete.fechaInicioSelected))
        const idReceta = filtrosPaquete.recetaSelected?.id ?? null
        const idDestino = filtrosPaquete.destinoSelected?.id ?? null
        const dateInicio = filtrosPaquete.fechaInicioSelected ? dayjs(filtrosPaquete.fechaInicioSelected) : null
        setPage(1)
        fetchData(idReceta, null, idDestino, 1, limit, fechaDesde, fechaHasta, dateInicio, null, null)
      } else {
        fetchData(null, null, null, 1, limit, fechaDesde, fechaHasta, null, null, null)
      }
    }

    inicializar()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Solo al montar; getUbicacion/getItemAtributoNoEliminados del contexto no son estables y provocan bucle infinito
  }, [])

  const refetchTablaReceta = useCallback(() => {
    const idReceta = recetaSelected ? recetaSelected.id : null
    const idDestino = destinoSelected ? destinoSelected.id : null
    let cerradoParam = null
    if (filtroCerrado) {
      cerradoParam = true
    } else if (filtroAbierto) {
      cerradoParam = false
    }
    const canceladoParam = filtroCancelado ? true : null

    fetchData(
      idReceta,
      null,
      idDestino,
      page,
      limit,
      fechaViajeDesde,
      fechaViajeHasta,
      fechaInicioSelected,
      cerradoParam,
      canceladoParam
    )
  }, [
    recetaSelected,
    destinoSelected,
    filtroCerrado,
    filtroAbierto,
    filtroCancelado,
    page,
    limit,
    fechaViajeDesde,
    fechaViajeHasta,
    fechaInicioSelected,
    fetchData,
  ])

  const handlePageChange = (newPage) => {
    const safeTotalPages = Math.max(Number(totalPages) || 1, 1)
    const nextPage = Math.min(Math.max(Number(newPage) || 1, 1), safeTotalPages)
    if (nextPage === page) return

    const idReceta = recetaSelected ? recetaSelected.id : null
    const idDestino = destinoSelected ? destinoSelected.id : null
    let cerradoParam = null
    if (filtroCerrado) cerradoParam = true
    else if (filtroAbierto) cerradoParam = false
    const canceladoParam = filtroCancelado ? true : null

    setPage(nextPage)
    fetchData(
      idReceta,
      null,
      idDestino,
      nextPage,
      limit,
      fechaViajeDesde,
      fechaViajeHasta,
      fechaInicioSelected,
      cerradoParam,
      canceladoParam
    )
  }



  // ? OPTIMIZADO: transformarRecetas con useCallback (para respuestas completas, p. ej. sin soloListado)
  const transformarRecetas = useCallback((recetasData) => {
    return recetasData.map((receta) => {
      const totalIngredientes = receta.ingredientes?.length || 0
      const cantidadTotal = receta.ingredientes?.reduce((sum, ing) => sum + (ing.cantidad || 0), 0) || 0

      // Obtener nombres de ingredientes para búsqueda
      const nombresIngredientes =
        receta.ingredientes?.map((ing) => ing.materiaPrima?.descripcion || "").join(", ") || ""

      return {
        id: receta.id,
        idPaquete: receta.idPaquete || receta.id, // ID del paquete (cabecera)
        descripcionProductoTerminado: receta.descripcionProductoTerminado || "Sin descripción",
        codigoProductoTerminado: receta.codigoProductoTerminado || "Sin código",
        idProductoTerminado: receta.idProductoTerminado,
        destino: receta.ubicacion?.descripcion || "Sin destino",
        butacas: Number(receta.butacas ?? receta.inventario ?? 0) || 0,
        destinoId: receta.destino || receta.destinoId, // ID del destino para cálculos
        fechaInicio: receta.fechaInicio ? dayjs(receta.fechaInicio).format("DD/MM/YYYY") : "N/A",
        fechaFin: receta.fechaFin ? dayjs(receta.fechaFin).format("DD/MM/YYYY") : "N/A",
        observaciones: receta.observaciones || "",
        noches: receta.noches || 0,
        cerrado: receta.cerrado || false, // Estado del paquete
        idVendedor: receta.idVendedor || null, // Vendedor del paquete
        vendedor: receta.vendedor || null, // Info del vendedor
        vendedores: Array.isArray(receta.vendedores) ? receta.vendedores : [],
        totalIngredientes,
        cantidadTotal: cantidadTotal.toFixed(2),
        ingredientes: receta.ingredientes || [],
        nombresIngredientes,
        // Agregar fechas formateadas
        fechaCreacion: receta.createdAt ? dayjs(receta.createdAt).format("DD/MM/YYYY") : "N/A",
        fechaActualizacion: receta.updatedAt ? dayjs(receta.updatedAt).format("DD/MM/YYYY") : "N/A",
        horaCreacion: receta.createdAt ? dayjs(receta.createdAt).format("HH:mm") : "N/A",
        horaActualizacion: receta.updatedAt ? dayjs(receta.updatedAt).format("HH:mm") : "N/A",
        // Para mostrar el primer ingrediente como referencia
        primerIngrediente: receta.ingredientes?.[0]?.materiaPrima?.descripcion || "Sin ingredientes",
        codigoPrimerIngrediente: receta.ingredientes?.[0]?.materiaPrima?.codigo || "",
        // Campos financieros (vienen precalculados desde backend)
        precioPaquete: Number(receta.precioPaquete || 0),
        // monto viene desde Paquete.monto; fallback a precioPaquete si no existe
        monto: receta.monto ?? receta.precioPaquete ?? 0,
        costoTotalServicios: Number(receta.costoTotalServicios || 0),
        ganancia: Number(receta.ganancia || 0),
        porcentajeGanancia: Number(receta.porcentajeGanancia || 0),
        pasajeros: Number(receta.pasajeros || 0),
        precioOriginal: Number.isFinite(Number(receta.precioOriginal))
        ? Number(receta.precioOriginal)
        : Number(receta.precioPaquete || 0) * Number(receta.pasajeros || 0),
        precioPorPasajeros: Number(receta.precioPorPasajeros || 0),
      }
    })
  }, [])

  // ? OPTIMIZADO: Función para cargar precios y costos con useCallback (MOVIDA ANTES DE handleOpenModal)
  const cargarPreciosCostos = useCallback(async (receta) => {
    try {
      // Cargar entidades una sola vez
      const proveedores = await getEntidadFilterTipoEntidad(2)
      // Precio del paquete: prioridad a cabecera (monto), fallback a precioPaquete
      const precioPaqueteValue = Number(receta.monto ?? receta.precioPaquete ?? 0)
      setPrecioPaquete(Number.isFinite(precioPaqueteValue) ? precioPaqueteValue : 0)

      // Cargar costos de servicios desde BOM (datos históricos)
      if (proveedores && proveedores.length > 0) {
        const nuevosPreciosCostos = {}
        receta.ingredientes.forEach((servicio) => {
          const key = `${servicio.idMateriaPrima}-${receta.destino}`
          // El monto en BOM es el costo TOTAL, necesitamos el costo UNITARIO para el modal
          const montoTotal = Number.parseFloat(servicio.monto || 0)
          const cantidad = Number.parseFloat(servicio.cantidad || 1)
          const costoUnitario = cantidad > 0 ? montoTotal / cantidad : montoTotal
          const idEntidadCosto = servicio.idEntidad || proveedores[0].id || null
          
          nuevosPreciosCostos[key] = { costo: costoUnitario, idEntidadCosto }
        })
        
        setPreciosCostos(nuevosPreciosCostos)
      }
    } catch (error) {
      console.error("Error cargando precios y costos:", error)
    }
  }, [getEntidadFilterTipoEntidad])

  const handleOpenModal = useCallback(async (receta) => {
    let recetaConIngredientes = receta
    if (!receta.ingredientes || receta.ingredientes.length === 0) {
      try {
        const bom = await getBomByIdItem(receta.idProductoTerminado, receta.destinoId ?? receta.destino, receta.idPaquete ?? receta.id)
        const ingredientes = Array.isArray(bom) ? bom : (bom?.servicios || bom?.data || [])
        recetaConIngredientes = { ...receta, ingredientes }
      } catch (err) {
        console.error("Error cargando ingredientes del paquete:", err)
        recetaConIngredientes = { ...receta, ingredientes: [] }
      }
    }
    setSelectedReceta(recetaConIngredientes)
    setEditedIngredientes((recetaConIngredientes.ingredientes || []).map((ing) => ({ ...ing })))

    setOpenModal(true)
    setCargandoDatosModal(true)
    setTimeout(async () => {
      await cargarPreciosCostos(recetaConIngredientes)
      setCargandoDatosModal(false)
    }, 100)
  }, [cargarPreciosCostos, getBomByIdItem])

  const handleCloseModal = useCallback(() => {
    setOpenModal(false)
    setSelectedReceta(null)
    setEditedIngredientes([])
    setPreciosCostos({})
    setPrecioPaquete(0)
    setCargandoDatosModal(false)
  }, [])

  // ? OPTIMIZADO: Funciones para modal de pasajeros con useCallback
  const handleOpenModalPasajeros = useCallback((receta) => {
    //console.log("?? Abriendo modal de pasajeros para receta:", receta)
    // Nota: si el paquete está cerrado se abre igual, pero en modo solo lectura (ver prop soloLectura).
    
    // Buscar el item completo en itemTresLetras para obtener atributos
    const itemCompleto = itemTresLetras?.find(it => it.id === receta.idProductoTerminado)
    
    // Formatear atributos como se hace en GestionGenerarClientesPaquete
    let attributeValues = ""
    if (itemCompleto && itemAtributoNoEliminados) {
      const activeAttributes = itemAtributoNoEliminados.filter((attr) => !attr.eliminado)
      attributeValues = activeAttributes
        .map((attr, index) => {
          const attributeValue = itemCompleto[`itemDatoAtributo${index + 1}`]
          return attributeValue ? attributeValue : ""
        })
        .filter((val) => val.trim() !== "")
        .join(" ")
    }
    
    // Formatear receta con el objeto grupo correctamente estructurado
    const recetaFormateada = {
      ...receta,
      grupoFormateado: {
        id: receta.idProductoTerminado,
        idItem: receta.idProductoTerminado,
        descripcion: receta.productoTerminado || itemCompleto?.descripcion || "",
        codigo: receta.codigoProductoTerminado || itemCompleto?.codigo || "",
        label: `${receta.codigoProductoTerminado || itemCompleto?.codigo || ""} ${receta.productoTerminado || itemCompleto?.descripcion || ""}${attributeValues ? " - " + attributeValues : ""}`,
        atributos: attributeValues,
      },
      destinoFormateado: ubicaciones?.find(ub => ub.id === receta.destinoId) ? {
        id: receta.destinoId,
        label: ubicaciones.find(ub => ub.id === receta.destinoId)?.descripcion || receta.destino,
      } : {
        id: receta.destinoId,
        label: receta.destino || "",
      }
    }
    
    setRecetaPasajeros(recetaFormateada)
    setOpenModalPasajeros(true)
  }, [itemTresLetras, itemAtributoNoEliminados, ubicaciones])

  const handleCloseModalPasajeros = useCallback(async () => {
    // Verificar si hay cambios pendientes antes de cerrar
    if (gestionPasajerosRef.current && gestionPasajerosRef.current.hayCambiosPendientes()) {
      const result = await Swal.fire({
        title: '¿Desea guardar los cambios?',
        text: 'Tiene cambios pendientes que no se han guardado. ¿Desea guardarlos antes de cerrar?',
        icon: 'question',
        showCancelButton: true,
        showDenyButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#6c757d',
        denyButtonColor: '#d33',
        confirmButtonText: 'Sí, guardar',
        denyButtonText: 'No guardar',
        cancelButtonText: 'Cancelar',
        reverseButtons: true,
        customClass: {
          container: 'swal-z-index-high'
        },
        didOpen: () => {
          // Asegurar z-index alto para que está por encima del modal de pasajeros
          const swalContainer = document.querySelector('.swal2-container')
          if (swalContainer) {
            swalContainer.style.zIndex = '9999'
          }
        }
      })

      if (result.isConfirmed) {
        // Si el usuario confirma, guardar los cambios
        try {
          await gestionPasajerosRef.current.guardarCambios()
          // Esperar un momento para que se complete el guardado
          await new Promise(resolve => setTimeout(resolve, 500))
          // Cerrar el modal después de guardar y recargar datos
          setOpenModalPasajeros(false)
          setRecetaPasajeros(null)
          refetchTablaReceta()
        } catch (error) {
          console.error("Error al guardar cambios:", error)
          await Swal.fire({
            title: 'Error',
            text: 'No se pudieron guardar los cambios. Por favor, intente nuevamente.',
            icon: 'error',
            confirmButtonText: 'Aceptar',
            customClass: {
              container: 'swal-z-index-high'
            },
            didOpen: () => {
              const swalContainer = document.querySelector('.swal2-container')
              if (swalContainer) {
                swalContainer.style.zIndex = '9999'
              }
            }
          })
          // No cerrar el modal si hay error
        }
      } else if (result.isDenied) {
        // Si el usuario elige "No guardar", cerrar sin guardar
        setOpenModalPasajeros(false)
        setRecetaPasajeros(null)
        refetchTablaReceta()
      }
      // Si cancela (result.isDismissed), no hacer nada (mantener el modal abierto)
    } else {
      // No hay cambios pendientes, cerrar normalmente
      setOpenModalPasajeros(false)
      setRecetaPasajeros(null)
      refetchTablaReceta()
    }
  }, [refetchTablaReceta])

  // Función para generar PDF de pasajeros
  const generatePassengersPDF = async (receta) => {
    try {
      const deliveryContext = { targetWindow: reservePdfTarget() }
      // Mostrar indicador de carga
      Swal.fire({
        title: 'Generando PDF...',
        text: 'Por favor espere',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading()
        }
      })

      // BOM bajo demanda: si la fila no trae ingredientes (listado ligero), cargarlos antes de generar el PDF
      let recetaConIngredientes = receta
      if (!receta.ingredientes?.length) {
        try {
          const bom = await getBomByIdItem(receta.idProductoTerminado, receta.destinoId ?? receta.destino, receta.idPaquete ?? receta.id)
          const ingredientes = Array.isArray(bom) ? bom : (bom?.servicios || bom?.data || [])
          recetaConIngredientes = { ...receta, ingredientes }
        } catch (err) {
          console.error("Error cargando ingredientes para PDF:", err)
          recetaConIngredientes = { ...receta, ingredientes: [] }
        }
      }

      // Vendedores BOM bajo demanda: evita usar vendedor legacy si el merge financiero aun no llegó
      if (!hasVendedoresBomData(recetaConIngredientes)) {
        try {
          const idPaquete = recetaConIngredientes.idPaquete ?? recetaConIngredientes.id
          if (idPaquete) {
            const { financieros } = await getTablaRecetaFinancieros([idPaquete])
            const fin = financieros?.[idPaquete] || financieros?.[recetaConIngredientes.id]
            if (fin) {
              recetaConIngredientes = mergeVendedoresFromFin(recetaConIngredientes, fin)
            }
          }
        } catch (err) {
          console.error("Error cargando vendedores financieros para PDF:", err)
        }
      }

      // Obtener pasajeros
      const pasajerosData = await getClientesPorGrupoYDestino(
        receta.idProductoTerminado,
        receta.destinoId
      )
      pasajerosData?.sort((a, b) => {
        const nA = `${a?.descripcion || ""} ${a?.apellido || ""}`.trim().toLowerCase()
        const nB = `${b?.descripcion || ""} ${b?.apellido || ""}`.trim().toLowerCase()
        return nA.localeCompare(nB, "es")
      })

      Swal.close()

      if (!pasajerosData || pasajerosData.length === 0) {
        await Swal.fire({
          title: 'Sin pasajeros',
          text: 'No hay pasajeros registrados para este viaje',
          icon: 'info',
          confirmButtonText: 'Aceptar'
        })
        return
      }

      const { logoTenant, showFechaSalida } = await fetchPdfConfig()

      // Crear PDF
      const doc = new jsPDF({ unit: "pt", format: "a4" })

      // Paleta de colores
      const PAL = {
        primary: [25, 118, 210],
        secondary: [120, 144, 156],
        success: [76, 175, 80],
        warning: [255, 152, 0],
        error: [244, 67, 54],
        ink: [33, 33, 33],
        muted: [117, 117, 117],
        inkLight: [158, 158, 158],
        bgSoft: [245, 247, 250],
      }
      const M = { l: 30, r: 30, t: 40, b: 40 }

      // Helpers
      const text = (str, x, y, { size = 10, color = PAL.ink, align = "left", bold = false } = {}) => {
        doc.setFont("helvetica", bold ? "bold" : "normal")
        doc.setFontSize(size)
        doc.setTextColor(...color)
        doc.text(str, x, y, { align })
      }

      const badge = (label, x, y, color = PAL.primary) => {
        doc.setFont("helvetica", "bold")
        doc.setFontSize(9)
        const padX = 8
        const w = doc.getTextWidth(label) + padX * 2
        const h = 16
        doc.setFillColor(...color)
        doc.roundedRect(x, y - h + 2, w, h, 8, 8, "F")
        doc.setTextColor(255, 255, 255)
        doc.text(label, x + padX, y - 5)
        doc.setTextColor(...PAL.ink)
      }

      const sectionTitle = (label, y) => {
        doc.setDrawColor(...PAL.primary)
        doc.setLineWidth(1)
        doc.line(M.l, y + 6, doc.internal.pageSize.getWidth() - M.r, y + 6)
        text(label, M.l, y, { size: 12, color: PAL.primary, bold: true })
      }

      const kvRow = (label, value) => [
        { content: label, styles: { halign: "left", fontStyle: "bold", textColor: PAL.muted } },
        { content: value ?? "N/A", styles: { halign: "left" } },
      ]

      const formatCurrency = (value) => {
        return new Intl.NumberFormat("es-AR", {
          style: "currency",
          currency: "ARS",
          maximumFractionDigits: 0,
        }).format(value)
      }

      const getImageFormat = (dataUrl) => {
        const match = /^data:image\/(png|jpe?g|webp);/i.exec(dataUrl || "")
        if (!match) return "PNG"
        const ext = match[1].toLowerCase()
        if (ext === "jpg" || ext === "jpeg") return "JPEG"
        if (ext === "webp") return "WEBP"
        return "PNG"
      }

      const resolveSaldo = (pasajero) => {
        const saldoPendiente = pasajero?.montoPendiente
        if (saldoPendiente !== null && saldoPendiente !== undefined && Number.isFinite(Number(saldoPendiente))) {
          return Number(saldoPendiente)
        }
        return 0
      }

      const resolveAbonado = (pasajero) => {
        const abonado = pasajero?.abonado
        if (abonado !== null && abonado !== undefined && Number.isFinite(Number(abonado))) {
          return Number(abonado)
        }
        return 0
      }

      const resolveTotal = (pasajero) => {
        const total = pasajero?.total
        if (total !== null && total !== undefined && Number.isFinite(Number(total))) {
          return Number(total)
        }
        return resolveAbonado(pasajero) + resolveSaldo(pasajero)
      }

      let proveedoresList = []
      try {
        const out = await getEntidadFilterTipoEntidad(2)
        proveedoresList = Array.isArray(out) ? out : []
      } catch (error) {
        console.error("Error cargando proveedores para PDF:", error)
      }
      const toEntidadKey = (value) => {
        if (value === null || value === undefined) return null
        return String(value)
      }
      const proveedoresMap = new Map(
        proveedoresList.map((prov) => [toEntidadKey(prov.id), prov])
      )

      const vendedoresDetalle = []
      const vendedoresSeen = new Set()
      ;(recetaConIngredientes.ingredientes || []).forEach((servicio) => {
        if (servicio?.esVendedor !== true) return
        const idEntidad = servicio?.idEntidad ?? servicio?.entidad?.id ?? null
        const entidadKey = toEntidadKey(idEntidad)
        const proveedor = entidadKey ? proveedoresMap.get(entidadKey) : null
        const nombre = [
          servicio?.entidad?.descripcion ?? proveedor?.descripcion,
          servicio?.entidad?.apellido ?? proveedor?.apellido,
        ]
          .filter(Boolean)
          .join(" ")
          .trim()
        if (!nombre) return
        const telefonoRaw = proveedor?.telefono ?? servicio?.entidad?.telefono ?? null
        const telefono = telefonoRaw == null ? "" : String(telefonoRaw).trim()
        const dedupeKey = `${entidadKey ?? "sin-id"}|${nombre}|${telefono}`
        if (vendedoresSeen.has(dedupeKey)) return
        vendedoresSeen.add(dedupeKey)
        vendedoresDetalle.push({ nombre, telefono })
      })

      const vendedoresBomFallback = Array.isArray(recetaConIngredientes?.vendedoresBomNombres)
        ? recetaConIngredientes.vendedoresBomNombres
            .map((nombre) => (typeof nombre === "string" ? nombre.trim() : ""))
            .filter(Boolean)
        : []
      const vendedoresHeaderLineas = vendedoresDetalle.length
        ? vendedoresDetalle.map((v) => (v.telefono ? `${v.nombre}  ${v.telefono}` : v.nombre))
        : vendedoresBomFallback.length
          ? vendedoresBomFallback
          : [getVendedoresTexto(recetaConIngredientes)]

      // Header
      const pageWidth = doc.internal.pageSize.getWidth()
      const pageHeight = doc.internal.pageSize.getHeight()

      if (logoTenant) {
        try {
          const logoMaxW = 90
          const logoMaxH = 42
          const props = doc.getImageProperties(logoTenant)
          const ratio = Math.min(logoMaxW / props.width, logoMaxH / props.height)
          const logoW = props.width * ratio
          const logoH = props.height * ratio
          doc.addImage(logoTenant, getImageFormat(logoTenant), M.l, 14, logoW, logoH)
        } catch (error) {
          // Ignorar errores de logo para no bloquear el PDF
        }
      }

      text("LISTA DE PASAJEROS", pageWidth / 2, 38, {
        size: 16,
        color: PAL.primary,
        align: "center",
        bold: true,
      })

      text(`Fecha de emisión: ${new Date().toLocaleDateString("es-AR")}`, pageWidth - M.r, 58, {
        size: 10,
        color: PAL.inkLight,
        align: "right",
      })

      text("Vendedor", pageWidth - M.r, 70, {
        size: 10,
        color: PAL.inkLight,
        align: "right",
      })
      const vendedorBaseY = 82
      vendedoresHeaderLineas.forEach((linea, index) => {
        text(linea || "-", pageWidth - M.r, vendedorBaseY + index * 11, {
          size: 10,
          color: PAL.ink,
          align: "right",
        })
      })

      // Sección 1: Información del Viaje
      const ultimoRenglonVendedorY = vendedorBaseY + (Math.max(vendedoresHeaderLineas.length, 1) - 1) * 11
      let cursorY = Math.max(120, ultimoRenglonVendedorY + 24)
      sectionTitle("INFORMACIÓN DEL VIAJE", cursorY)
      cursorY += 18

      doc.autoTable({
        startY: cursorY,
        margin: { left: M.l, right: M.r },
        theme: "plain",
        styles: { font: "helvetica", fontSize: 9, cellPadding: 2, textColor: PAL.ink },
        columnStyles: { 0: { cellWidth: 140 } },
        body: [
          kvRow("Grupo", `${receta.codigoProductoTerminado || ""} - ${receta.descripcionProductoTerminado || ""}`),
          kvRow("Destino", receta.destino || "N/A"),
          ...(showFechaSalida
            ? [
                kvRow("Fecha de inicio de viaje", receta.fechaInicio || "N/A"),
                kvRow("Fecha de fin", receta.fechaFin || "N/A"),
              ]
            : []),
          kvRow("Noches", String(receta.noches || 0)),
        ],
      })

      cursorY = doc.lastAutoTable.finalY + 18

      // Sección 2: Tabla de Pasajeros
      sectionTitle("PASAJEROS", cursorY)
      cursorY += 20

      const pasajerosRows = pasajerosData.map((pasajero, index) => {
        const nombreCompleto = `${pasajero.descripcion || ""} ${pasajero.apellido || ""}`.trim()
        const total = resolveTotal(pasajero)
        const abonado = resolveAbonado(pasajero)
        const saldo = resolveSaldo(pasajero)
        
        return [
          String(index + 1),
          nombreCompleto,
          pasajero.dniCuitCuil || "-",
          pasajero.viaja !== false ? "Sí" : "No",
          pasajero.liberado === true ? "Sí" : "No",
          formatCurrency(total),
          formatCurrency(abonado),
          formatCurrency(saldo),
        ]
      })

      doc.autoTable({
        startY: cursorY,
        margin: { left: M.l, right: M.r },
        theme: "striped",
        head: [["#", "Pasajero", "DNI/CUIT", "Viaja", "Liberado", "Total", "Abonado", "A Pagar"]],
        body: pasajerosRows,
        styles: { font: "helvetica", fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: PAL.primary, textColor: [255, 255, 255], fontStyle: "bold" },
        columnStyles: {
          0: { cellWidth: 25, halign: "center" },
          1: { cellWidth: 150 },
          2: { cellWidth: 70 },
          3: { cellWidth: 40, halign: "center" },
          4: { cellWidth: 45, halign: "center" },
          5: { cellWidth: 65, halign: "right" },
          6: { cellWidth: 65, halign: "right" },
          7: { cellWidth: 65, halign: "right" },
        },
      })

      cursorY = doc.lastAutoTable.finalY + 18

      // Sección 3: Servicios Asociados al Paquete
      sectionTitle("SERVICIOS ASOCIADOS AL PAQUETE", cursorY)
      cursorY += 20

      const serviciosRows = (recetaConIngredientes.ingredientes || []).map((servicio, index) => {
        const descripcion = servicio.materiaPrima?.descripcion || "Sin descripción"
        const proveedorKey = toEntidadKey(servicio.idEntidad)
        const proveedor = (proveedorKey ? proveedoresMap.get(proveedorKey) : null)?.descripcion || "-"
        const costo = Number(servicio.monto || 0)

        return [
          String(index + 1),
          descripcion,
          proveedor,
          formatCurrency(costo),
        ]
      })

      if (serviciosRows.length === 0) {
        text("No hay servicios asociados a este grupo.", M.l, cursorY, { size: 10, color: PAL.muted })
        cursorY += 20
      } else {
        doc.autoTable({
          startY: cursorY,
          margin: { left: M.l, right: M.r },
          theme: "striped",
          head: [["#", "Servicio", "Proveedor", "Costo"]],
          body: serviciosRows,
          styles: { font: "helvetica", fontSize: 8, cellPadding: 2 },
          headStyles: { fillColor: PAL.primary, textColor: [255, 255, 255], fontStyle: "bold" },
          columnStyles: {
            0: { cellWidth: 25, halign: "center" },
            1: { cellWidth: 240 },
            2: { cellWidth: 140 },
            3: { cellWidth: 80, halign: "right" },
          },
        })

        cursorY = doc.lastAutoTable.finalY + 18
      }

      // Sección 4: Estadísticas
      sectionTitle("ESTADISTICAS", cursorY)
      cursorY += 18

      const costoTotalServicios = Number(recetaConIngredientes.costoTotalServicios ?? 0)
      const totalAbonado = Number(recetaConIngredientes.precioPorPasajeros ?? 0)
      const gananciaCalc = Number(recetaConIngredientes.ganancia ?? (totalAbonado - costoTotalServicios))

      doc.autoTable({
        startY: cursorY,
        margin: { left: M.l, right: M.r },
        theme: "plain",
        styles: { font: "helvetica", fontSize: 9, cellPadding: 2, textColor: PAL.ink },
        columnStyles: { 0: { cellWidth: 160 } },
        body: [
          kvRow("Total abonado", formatCurrency(totalAbonado)),
          kvRow("Total Costo", formatCurrency(costoTotalServicios)),
          kvRow("Ganancia", formatCurrency(gananciaCalc)),
        ],
      })

      cursorY = doc.lastAutoTable.finalY + 18

      // Footer
      const footerY = pageHeight - 70
      doc.setDrawColor(...PAL.secondary)
      doc.setLineWidth(0.4)
      doc.line(M.l, footerY, pageWidth - M.r, footerY)
      text("Documento informativo y No válido como comprobante fiscal", pageWidth / 2, footerY + 18, {
        size: 9,
        color: PAL.inkLight,
        align: "center",
      })
      text("Consultas: contactar al administrador del sistema.", pageWidth / 2, footerY + 32, {
        size: 9,
        color: PAL.inkLight,
        align: "center",
      })

      // Metadata
      doc.setProperties({
        title: `Pasajeros_${receta.codigoProductoTerminado}_${receta.destino}`,
        subject: "Lista de Pasajeros",
        author: "LUTENTE TOURS",
        creator: "LUTENTE TOURS",
      })

      const fileName = `pasajeros_${receta.codigoProductoTerminado}_${receta.destino}_${new Date().toISOString().slice(0, 10)}.pdf`
      deliverPdf({ doc, fileName, targetWindow: deliveryContext.targetWindow })

    } catch (error) {
      console.error("Error generando PDF de pasajeros:", error)
      Swal.close()
      await Swal.fire({
        title: 'Error',
        text: 'No se pudo generar el PDF. Por favor, intente nuevamente.',
        icon: 'error',
        confirmButtonText: 'Aceptar'
      })
    }
  }

  // PDF solo de clientes con monto a pagar
  const generateClientesPDF = async (receta) => {
    try {
      Swal.fire({
        title: 'Generando PDF...',
        text: 'Por favor espere',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading()
        }
      })

      const pasajerosData = await getClientesPorGrupoYDestino(
        receta.idProductoTerminado,
        receta.destinoId
      )
      pasajerosData?.sort((a, b) => {
        const nA = `${a?.descripcion || ""} ${a?.apellido || ""}`.trim().toLowerCase()
        const nB = `${b?.descripcion || ""} ${b?.apellido || ""}`.trim().toLowerCase()
        return nA.localeCompare(nB, "es")
      })

      if (!pasajerosData || pasajerosData.length === 0) {
        Swal.close()
        await Swal.fire({
          title: 'Sin pasajeros',
          text: 'No hay pasajeros registrados para este viaje',
          icon: 'info',
          confirmButtonText: 'Aceptar'
        })
        return
      }

      // Cargar ingredientes (BOM) si no vienen en la receta
      let recetaConIngredientes = receta
      if (!receta.ingredientes?.length) {
        try {
          const bom = await getBomByIdItem(receta.idProductoTerminado, receta.destinoId ?? receta.destino, receta.idPaquete ?? receta.id)
          const ingredientes = Array.isArray(bom) ? bom : (bom?.servicios || bom?.data || [])
          recetaConIngredientes = { ...receta, ingredientes }
        } catch (err) {
          console.error("Error cargando ingredientes para PDF:", err)
          recetaConIngredientes = { ...receta, ingredientes: [] }
        }
      }

      // Vendedores BOM bajo demanda
      if (!hasVendedoresBomData(recetaConIngredientes)) {
        try {
          const idPaquete = recetaConIngredientes.idPaquete ?? recetaConIngredientes.id
          if (idPaquete) {
            const { financieros } = await getTablaRecetaFinancieros([idPaquete])
            const fin = financieros?.[idPaquete] || financieros?.[recetaConIngredientes.id]
            if (fin) {
              recetaConIngredientes = mergeVendedoresFromFin(recetaConIngredientes, fin)
            }
          }
        } catch (err) {
          console.error("Error cargando vendedores financieros para PDF:", err)
        }
      }

      // Construir vendedoresDetalle (nombre + teléfono) como en TablaVerBalance
      let proveedoresList = []
      try {
        const out = await getEntidadFilterTipoEntidad(2)
        proveedoresList = Array.isArray(out) ? out : []
      } catch (error) {
        console.error("Error cargando proveedores para PDF:", error)
      }
      const toEntidadKey = (value) => (value == null ? null : String(value))
      const proveedoresMap = new Map(proveedoresList.map((prov) => [toEntidadKey(prov.id), prov]))

      const vendedoresDetalle = []
      const vendedoresSeen = new Set()
      ;(recetaConIngredientes.ingredientes || []).forEach((servicio) => {
        if (servicio?.esVendedor !== true) return
        const idEntidad = servicio?.idEntidad ?? servicio?.entidad?.id ?? null
        const entidadKey = toEntidadKey(idEntidad)
        const proveedor = entidadKey ? proveedoresMap.get(entidadKey) : null
        const nombre = [
          servicio?.entidad?.descripcion ?? proveedor?.descripcion,
          servicio?.entidad?.apellido ?? proveedor?.apellido,
        ]
          .filter(Boolean)
          .join(" ")
          .trim()
        if (!nombre) return
        const telefonoRaw = proveedor?.telefono ?? servicio?.entidad?.telefono ?? null
        const telefono = telefonoRaw == null ? "" : String(telefonoRaw).trim()
        const dedupeKey = `${entidadKey ?? "sin-id"}|${nombre}|${telefono}`
        if (vendedoresSeen.has(dedupeKey)) return
        vendedoresSeen.add(dedupeKey)
        vendedoresDetalle.push({ nombre, telefono })
      })

      const vendedoresBomFallback = Array.isArray(recetaConIngredientes?.vendedoresBomNombres)
        ? recetaConIngredientes.vendedoresBomNombres
            .map((nombre) => (typeof nombre === "string" ? nombre.trim() : ""))
            .filter(Boolean)
        : []
      const vendedoresHeaderLineas =
        vendedoresDetalle.length > 0
          ? vendedoresDetalle.map((v) => (v.telefono ? `${v.nombre}  ${v.telefono}` : v.nombre))
          : vendedoresBomFallback.length > 0
            ? vendedoresBomFallback
            : [getVendedoresTexto(recetaConIngredientes)]

      Swal.close()

      const { logoTenant, showFechaSalida } = await fetchPdfConfig()
      generarPdfClientesReceta({
        receta,
        pasajerosData,
        logoBase64: logoTenant,
        vendedoresHeaderLineas,
        showFechaSalida,
      })
    } catch (error) {
      console.error("Error generando PDF de clientes:", error)
      Swal.close()
      await Swal.fire({
        title: 'Error',
        text: 'No se pudo generar el PDF. Por favor, intente nuevamente.',
        icon: 'error',
        confirmButtonText: 'Aceptar'
      })
    }
  }

  const exportarClientesExcel = async (receta) => {
    try {
      Swal.fire({
        title: 'Generando Excel...',
        text: 'Por favor espere',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading()
        }
      })

      const pasajerosData = await getClientesPorGrupoYDestino(
        receta.idProductoTerminado,
        receta.destinoId
      )
      pasajerosData?.sort((a, b) => {
        const nA = `${a?.descripcion || ""} ${a?.apellido || ""}`.trim().toLowerCase()
        const nB = `${b?.descripcion || ""} ${b?.apellido || ""}`.trim().toLowerCase()
        return nA.localeCompare(nB, "es")
      })

      Swal.close()

      if (!pasajerosData || pasajerosData.length === 0) {
        await Swal.fire({
          title: 'Sin pasajeros',
          text: 'No hay pasajeros registrados para este viaje',
          icon: 'info',
          confirmButtonText: 'Aceptar'
        })
        return
      }

      // Función para calcular edad
      const calcularEdad = (fechaNacimiento) => {
        if (!fechaNacimiento) return null
        const hoy = new Date()
        const nacimiento = new Date(fechaNacimiento)
        let edad = hoy.getFullYear() - nacimiento.getFullYear()
        const mes = hoy.getMonth() - nacimiento.getMonth()
        if (mes < 0 || (mes === 0 && hoy.getDate() < nacimiento.getDate())) {
          edad--
        }
        return edad
      }

      // Preparar datos para el Excel
      const workbook = XLSX.utils.book_new()
      
      // Crear datos con encabezados del grupo
      const datosExcel = []
      
      // Agregar filas de encabezado del viaje
      datosExcel.push(['GRUPO:', receta.codigoProductoTerminado || ''])
      datosExcel.push(['DESTINO:', receta.destino || ''])
      datosExcel.push(['FECHA VIAJE:', receta.fechaInicio || ''])
      datosExcel.push(['LUGAR DE SALIDA:', '']) // Campo no disponible actualmente
      datosExcel.push(['MICRO:', '']) // Campo no disponible actualmente
      datosExcel.push([]) // Línea en blanco
      
      // Agregar encabezados de la tabla de pasajeros
      datosExcel.push(['#', 'NOMBRE', 'APELLIDO', 'DNI', 'FECHA NAC', 'NACIONALIDAD', 'SEXO', 'MENOR'])
      
      // Agregar los pasajeros
      pasajerosData.forEach((pasajero, index) => {
        const edad = calcularEdad(pasajero.fechaNacimiento)
        const esMenor = edad !== null && edad < 18 ? 'MENOR' : 'MAYOR'
        
        // Formatear fecha de nacimiento
        let fechaNacFormateada = ''
        if (pasajero.fechaNacimiento) {
          try {
            fechaNacFormateada = dayjs(pasajero.fechaNacimiento).format('DD/MM/YYYY')
          } catch (error) {
            fechaNacFormateada = pasajero.fechaNacimiento
          }
        }
        
        datosExcel.push([
          index + 1,
          pasajero.nombre || '',
          pasajero.apellido || '',
          pasajero.dniCuitCuil || '',
          fechaNacFormateada,
          pasajero.pais || '',
          pasajero.sexo || '',
          esMenor
        ])
      })
      
      // Crear la hoja de cálculo
      const worksheet = XLSX.utils.aoa_to_sheet(datosExcel)
      
      // Ajustar anchos de columna
      const columnWidths = [
        { wch: 5 },  // #
        { wch: 20 }, // NOMBRE
        { wch: 20 }, // APELLIDO
        { wch: 12 }, // DNI
        { wch: 12 }, // FECHA NAC
        { wch: 15 }, // NACIONALIDAD
        { wch: 10 }, // SEXO
        { wch: 10 }  // MENOR
      ]
      worksheet['!cols'] = columnWidths
      
      // Agregar la hoja al libro
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Pasajeros')
      
      // Generar nombre de archivo
      const nombreGrupo = receta.codigoProductoTerminado || 'Grupo'
      const destino = receta.destino || 'Destino'
      const fecha = receta.fechaInicio || dayjs().format('YYYY-MM-DD')
      const nombreArchivo = `Pasajeros_${nombreGrupo}_${destino}_${fecha}.xlsx`
      
      // Descargar el archivo
      XLSX.writeFile(workbook, nombreArchivo)

      await Swal.fire({
        title: 'Excel generado',
        text: 'El archivo se ha descargado correctamente',
        icon: 'success',
        confirmButtonText: 'Aceptar'
      })
    } catch (error) {
      console.error("Error generando Excel de clientes:", error)
      Swal.close()
      await Swal.fire({
        title: 'Error',
        text: 'No se pudo generar el Excel. Por favor, intente nuevamente.',
        icon: 'error',
        confirmButtonText: 'Aceptar'
      })
    }
  }

  const handleCantidadChange = useCallback((index, newCantidad) => {
    const updated = [...editedIngredientes]
    updated[index] = {
      ...updated[index],
      cantidad: Number.parseFloat(newCantidad) || 0,
    }
    setEditedIngredientes(updated)
  }, [editedIngredientes])

  const handleSaveChanges = useCallback(async () => {
  //console.log("Receta seleccionada", selectedReceta);
  //console.log("Guardando cambios:", editedIngredientes);

  try {
    setLoading(true);

    const ingredientesConCantidad = editedIngredientes.map((item) => {
      return {
        idItem: item.idMateriaPrima,
        cantidad: item.cantidad || "0",
        idUbicacion: item.idUbicacion || 1,
      };
    });

    const idProductoTerminado = selectedReceta.idProductoTerminado;
    const destino = selectedReceta.destinoId ?? selectedReceta.destino;
    const idPaquete = selectedReceta.idPaquete ?? selectedReceta.id;
    await crearRecetaApi(
      idProductoTerminado,
      ingredientesConCantidad,
      true,
      destino,
      selectedReceta.fechaInicioRaw ?? selectedReceta.fechaInicio,
      selectedReceta.fechaFinRaw ?? selectedReceta.fechaFin,
      selectedReceta.noches ?? null,
      idPaquete,
      null,
      selectedReceta.monto ?? null
    );

  } catch (error) {
    console.error("Error creando o actualizando receta:", error);
  } finally {
    setLoading(false);
    handleCloseModal();
    const idReceta = recetaSelected ? recetaSelected.id : null
    const idDestino = destinoSelected ? destinoSelected.id : null
    let cerradoParam = null
    if (filtroCerrado) cerradoParam = true
    else if (filtroAbierto) cerradoParam = false
    const canceladoParam = filtroCancelado ? true : null
    fetchData(idReceta, null, idDestino, page, limit, fechaViajeDesde, fechaViajeHasta, fechaInicioSelected, cerradoParam, canceladoParam)
  }
  }, [editedIngredientes, selectedReceta, crearRecetaApi, handleCloseModal, recetaSelected, destinoSelected, filtroCerrado, filtroAbierto, filtroCancelado, fetchData, page, limit, fechaViajeDesde, fechaViajeHasta, fechaInicioSelected])

  const limpiarFiltros = useCallback(() => {
    setSearchTextReceta("")
    setRecetaSelected(null)
    setDestinoSelected(null)
    setFiltroVendedor("")
    setFiltroProducto("")
    setFiltroDestino("")
    const today = dayjs()
    // Reinicializar fechas de viaje con el año actual (1/1 a 31/12)
    setFechaViajeDesde(today.startOf("year"))
    setFechaViajeHasta(today.endOf("year"))
    // Limpiar filtro de fecha inicio
    setFechaInicioSelected(null)
    // Limpiar filtros de estado
    setFiltroCerrado(false)
    setFiltroAbierto(false)
    setFiltroCancelado(false)
  }, [])

  const aplicarFiltros = useCallback(() => {
    const idReceta = recetaSelected ? recetaSelected.id : null
    const idDestino = destinoSelected ? destinoSelected.id : null
    let cerradoParam = null
    if (filtroCerrado) {
      cerradoParam = true
    } else if (filtroAbierto) {
      cerradoParam = false
    }
    const canceladoParam = filtroCancelado ? true : null
    setPage(1)
    fetchData(idReceta, null, idDestino, 1, limit, fechaViajeDesde, fechaViajeHasta, fechaInicioSelected, cerradoParam, canceladoParam)
  }, [recetaSelected, destinoSelected, filtroCerrado, filtroAbierto, filtroCancelado, limit, fechaViajeDesde, fechaViajeHasta, fechaInicioSelected, fetchData])

  const handleExportarPdf = useCallback(async () => {
    try {
      setLoading(true)
      let cerradoParam = null
      if (filtroCerrado) cerradoParam = true
      else if (filtroAbierto) cerradoParam = false
      const filtros = {
        idReceta: recetaSelected?.id ?? null,
        idMateriaPrima: null,
        idDestino: destinoSelected?.id ?? null,
        fechaViajeDesde: fechaViajeDesde || null,
        fechaViajeHasta: fechaViajeHasta || null,
        fechaInicio: fechaInicioSelected || null,
        cerrado: cerradoParam,
        receta: recetaSelected || null,
        destino: destinoSelected || null,
      }
      const params = await getParametrosGlobales(["logoNavbarTenant"])
      const logoBase64 = params?.logoNavbarTenant || null
      await generarPdfRecetas(filtros, logoBase64, user?.usuario)
    } catch (error) {
      console.error("Error al generar PDF de recetas:", error)
      alert("Error al generar el PDF")
    } finally {
      setLoading(false)
    }
  }, [recetaSelected, destinoSelected, fechaViajeDesde, fechaViajeHasta, fechaInicioSelected, filtroCerrado, filtroAbierto, generarPdfRecetas, getParametrosGlobales, user])

  // ?? Manejar cambio de checkbox Cerrado (mutuamente excluyente)
  const handleFiltroCerradoChange = useCallback((event) => {
    const checked = event.target.checked
    if (checked) {
      setFiltroCerrado(true)
      setFiltroAbierto(false)
      setFiltroCancelado(false)
    } else {
      setFiltroCerrado(false)
    }
    
    // Aplicar filtro automáticamente
    const idReceta = recetaSelected ? recetaSelected.id : null
    const idDestino = destinoSelected ? destinoSelected.id : null
    const cerradoParam = checked ? true : null
    setPage(1)
    fetchData(idReceta, null, idDestino, 1, limit, fechaViajeDesde, fechaViajeHasta, fechaInicioSelected, cerradoParam, null)
  }, [recetaSelected, destinoSelected, limit, fechaViajeDesde, fechaViajeHasta, fechaInicioSelected, fetchData])

  // ?? Manejar cambio de checkbox Abierto (mutuamente excluyente)
  const handleFiltroAbiertoChange = useCallback((event) => {
    const checked = event.target.checked
    if (checked) {
      setFiltroAbierto(true)
      setFiltroCerrado(false)
      setFiltroCancelado(false)
    } else {
      setFiltroAbierto(false)
    }
    
    // Aplicar filtro automáticamente
    const idReceta = recetaSelected ? recetaSelected.id : null
    const idDestino = destinoSelected ? destinoSelected.id : null
    const cerradoParam = checked ? false : null
    setPage(1)
    fetchData(idReceta, null, idDestino, 1, limit, fechaViajeDesde, fechaViajeHasta, fechaInicioSelected, cerradoParam, null)
  }, [recetaSelected, destinoSelected, limit, fechaViajeDesde, fechaViajeHasta, fechaInicioSelected, fetchData])

  // Manejar cambio de checkbox Cancelado (mutuamente excluyente)
  const handleFiltroCanceladoChange = useCallback((event) => {
    const checked = event.target.checked
    if (checked) {
      setFiltroCancelado(true)
      setFiltroCerrado(false)
      setFiltroAbierto(false)
    } else {
      setFiltroCancelado(false)
    }
    
    // Aplicar filtro automáticamente
    const idReceta = recetaSelected ? recetaSelected.id : null
    const idDestino = destinoSelected ? destinoSelected.id : null
    const canceladoParam = checked ? true : null
    setPage(1)
    fetchData(idReceta, null, idDestino, 1, limit, fechaViajeDesde, fechaViajeHasta, fechaInicioSelected, null, canceladoParam)
  }, [recetaSelected, destinoSelected, limit, fechaViajeDesde, fechaViajeHasta, fechaInicioSelected, fetchData])

  // ?? Toggle estado del paquete (sin guardar inmediatamente - batch)
  const handleToggleEstadoPaquete = useCallback((paquete) => {
    setCambiosPendientes(prev => {
      const newMap = new Map(prev)
      const idPaquete = paquete.idPaquete
      
      if (newMap.has(idPaquete)) {
        // Si ya está en cambios pendientes, verificar si vuelve al estado original
        const cambio = newMap.get(idPaquete)
        if (cambio.estadoOriginal === !paquete.cerrado) {
          // Vuelve al estado original, quitar de cambios pendientes
          newMap.delete(idPaquete)
        } else {
          // Actualizar estado nuevo
          newMap.set(idPaquete, {
            ...cambio,
            estadoNuevo: !paquete.cerrado
          })
        }
      } else {
        // Agregar nuevo cambio pendiente
        newMap.set(idPaquete, {
          estadoOriginal: paquete.cerrado,
          estadoNuevo: !paquete.cerrado,
          codigoProductoTerminado: paquete.codigoProductoTerminado,
          descripcionProductoTerminado: paquete.descripcionProductoTerminado,
          destino: paquete.destino,
          fechaInicio: paquete.fechaInicio,
          fechaFin: paquete.fechaFin,
          noches: paquete.noches,
          pasajeros: paquete.pasajeros,
          precioPorPasajeros: paquete.precioPorPasajeros,
          costoTotalServicios: paquete.costoTotalServicios,
          ganancia: paquete.ganancia
        })
      }
      
      return newMap
    })
  }, [])

  // Función para resaltar filas con cambios pendientes / sobrecapacidad
  const getRowClassName = useCallback((params) => {
    const rowClasses = []

    if (cambiosPendientes.has(params.row.idPaquete)) {
      rowClasses.push("fila-cambio-pendiente")
    }

    const pasajerosConfirmados = Number(params.row?.pasajeros ?? 0)
    const butacas = Number(params.row?.butacas ?? 0)
    if (pasajerosConfirmados > butacas) {
      rowClasses.push("fila-sobrecapacidad")
    }

    return rowClasses.join(" ")
  }, [cambiosPendientes])

  // Función para generar PDF de cambios de estado
  const generarPDFCambiosEstado = useCallback(async (cambios) => {
    const logoTenant = await fetchLogoTenant()
    // PDF en orientación horizontal para más columnas
    const doc = new jsPDF('landscape')
    const fecha = dayjs().format("DD/MM/YYYY HH:mm")
    
    // Configuración de colores y márgenes
    const PAL = {
      primary: [91, 91, 95],
      secondary: [244, 177, 14],
      success: [76, 175, 80],
      warning: [255, 128, 66],
      ink: [60, 60, 60],
      muted: [130, 130, 130]
    }
    const M = { l: 15, r: 15, t: 20, b: 20 }

    const getImageFormat = (dataUrl) => {
      const match = /^data:image\/(png|jpe?g|webp);/i.exec(dataUrl || "")
      if (!match) return "PNG"
      const ext = match[1].toLowerCase()
      if (ext === "jpg" || ext === "jpeg") return "JPEG"
      if (ext === "webp") return "WEBP"
      return "PNG"
    }
    
    // Separar cambios por tipo
    const cambiosCerrados = cambios.filter(c => c.estadoNuevo === true)
    const cambiosAbiertos = cambios.filter(c => c.estadoNuevo === false)
    
    // Header
    doc.setFillColor(...PAL.primary)
    doc.rect(0, 0, 297, 35, "F") // 297 = ancho en landscape
    if (logoTenant) {
      try {
        const logoMaxW = 50
        const logoMaxH = 20
        const props = doc.getImageProperties(logoTenant)
        const ratio = Math.min(logoMaxW / props.width, logoMaxH / props.height)
        const logoW = props.width * ratio
        const logoH = props.height * ratio
        doc.addImage(logoTenant, getImageFormat(logoTenant), M.l, 8, logoW, logoH)
      } catch (error) {
        // Ignorar errores de logo para no bloquear el PDF
      }
    }
    doc.setTextColor(255, 255, 255)
    doc.setFontSize(18)
    doc.setFont("helvetica", "bold")
    doc.text("CAMBIOS DE ESTADO DE PAQUETES", 148.5, 15, { align: "center" })
    doc.setFontSize(10)
    doc.setFont("helvetica", "normal")
    doc.text(`Fecha de emisión: ${fecha}`, 148.5, 28, { align: "center" })
    
    let cursorY = 45
    
    // Información general
    doc.setTextColor(...PAL.ink)
    doc.setFontSize(11)
    doc.setFont("helvetica", "bold")
    doc.text(`Total de paquetes modificados: ${cambios.length}`, M.l, cursorY)
    doc.setFontSize(10)
    doc.setFont("helvetica", "normal")
    doc.text(`Cerrados: ${cambiosCerrados.length}  |  Abiertos: ${cambiosAbiertos.length}`, M.l, cursorY + 6)
    cursorY += 16
    
    // Función helper para crear tabla
    const crearTabla = (datos, titulo, color) => {
      if (datos.length === 0) return
      
      // Título de sección
      doc.setFillColor(...color)
      doc.rect(M.l, cursorY, 267, 8, "F") // 267 = 297 - márgenes
      doc.setTextColor(255, 255, 255)
      doc.setFontSize(10)
      doc.setFont("helvetica", "bold")
      doc.text(titulo, M.l + 3, cursorY + 5.5)
      cursorY += 10
      
      // Preparar datos con formato de moneda
      const tableData = datos.map((cambio, index) => [
        String(index + 1),
        cambio.codigoProductoTerminado,
        cambio.descripcionProductoTerminado,
        cambio.destino,
        cambio.fechaInicio || "-",
        cambio.fechaFin || "-",
        String(cambio.noches || 0),
        String(cambio.pasajeros || 0),
        formatearMoneda(cambio.precioPorPasajeros || 0),
        formatearMoneda(cambio.costoTotalServicios || 0),
        formatearMoneda(cambio.ganancia || 0)
      ])
      
      doc.autoTable({
        startY: cursorY,
        margin: { left: M.l, right: M.r },
        head: [["#", "Código", "Grupo", "Destino", "F. Inicio", "F. Fin", "Noches", "Pasaj.", "Precio", "Costo", "Ganancia"]],
        body: tableData,
        theme: "striped",
        headStyles: {
          fillColor: PAL.primary,
          textColor: [255, 255, 255],
          fontStyle: "bold",
          fontSize: 8
        },
        styles: {
          font: "helvetica",
          fontSize: 7,
          cellPadding: 1.5,
          overflow: "linebreak"
        },
        columnStyles: {
          0: { cellWidth: 8, halign: "center" },     // #
          1: { cellWidth: 18, halign: "center" },    // Código
          2: { cellWidth: 50 },                      // Grupo
          3: { cellWidth: 35 },                      // Destino
          4: { cellWidth: 20, halign: "center" },    // F. Inicio
          5: { cellWidth: 20, halign: "center" },    // F. Fin
          6: { cellWidth: 14, halign: "center" },    // Noches
          7: { cellWidth: 14, halign: "center" },    // Pasajeros
          8: { cellWidth: 25, halign: "right" },     // Precio
          9: { cellWidth: 25, halign: "right" },     // Costo
          10: { cellWidth: 25, halign: "right" }     // Ganancia
        }
      })
      
      cursorY = doc.lastAutoTable.finalY + 12
      
      // Si se pasa de página, agregar espacio
      if (cursorY > 190) { // Ajustado para landscape
        doc.addPage()
        cursorY = 20
      }
    }
    
    // Tabla de paquetes CERRADOS
    if (cambiosCerrados.length > 0) {
      crearTabla(cambiosCerrados, `PAQUETES CERRADOS (${cambiosCerrados.length})`, PAL.success)
    }
    
    // Tabla de paquetes ABIERTOS
    if (cambiosAbiertos.length > 0) {
      crearTabla(cambiosAbiertos, `PAQUETES ABIERTOS (${cambiosAbiertos.length})`, PAL.warning)
    }
    
    // Footer en todas las páginas
    const pageCount = doc.internal.getNumberOfPages()
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i)
      doc.setFontSize(8)
      doc.setTextColor(...PAL.muted)
      doc.text(
        `Página ${i} de ${pageCount}`,
        doc.internal.pageSize.width / 2,
        doc.internal.pageSize.height - 10,
        { align: "center" }
      )
    }
    
    // Guardar PDF
    const nombreArchivo = `cambios_estado_${dayjs().format("YYYY-MM-DD_HHmm")}.pdf`
    doc.save(nombreArchivo)
  }, [])

  // Función para guardar cambios en batch
  const handleGuardarEstados = useCallback(async () => {
    if (cambiosPendientes.size === 0) return
    
    try {
      Swal.fire({
        title: `Guardando ${cambiosPendientes.size} cambio(s)...`,
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading()
        }
      })
      
      // Guardar todos los cambios en paralelo
      const promesas = Array.from(cambiosPendientes.entries()).map(([idPaquete, cambio]) => 
        setCerradoPaqueteApi(idPaquete, cambio.estadoNuevo)
      )
      
      await Promise.all(promesas)
      
      // Generar PDF con los cambios
      await generarPDFCambiosEstado(Array.from(cambiosPendientes.values()))
      
      Swal.fire({
        icon: 'success',
        title: 'Cambios guardados',
        text: `Se actualizaron ${cambiosPendientes.size} paquete(s). El PDF se ha descargado automáticamente.`,
        timer: 3000,
        showConfirmButton: false
      })
      
      // Limpiar cambios pendientes
      setCambiosPendientes(new Map())
      
      // Recargar tabla
      const idReceta = recetaSelected ? recetaSelected.id : null
      const idDestino = destinoSelected ? destinoSelected.id : null
      let cerradoParam = null
      if (filtroCerrado) cerradoParam = true
      else if (filtroAbierto) cerradoParam = false
      const canceladoParam = filtroCancelado ? true : null
      
      await fetchData(
        idReceta,
        null,
        idDestino,
        page,
        limit,
        fechaViajeDesde,
        fechaViajeHasta,
        fechaInicioSelected,
        cerradoParam,
        canceladoParam
      )
      
    } catch (error) {
      console.error("Error al guardar cambios:", error)
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudieron guardar algunos cambios. Por favor, revise e intente nuevamente.'
      })
    }
  }, [cambiosPendientes, setCerradoPaqueteApi, recetaSelected, destinoSelected, filtroCerrado, filtroAbierto, page, limit, fechaViajeDesde, fechaViajeHasta, fechaInicioSelected, fetchData, generarPDFCambiosEstado])

  const hayFiltrosActivos =
    searchTextReceta ||
    recetaSelected ||
    destinoSelected ||
    filtroVendedor ||
    fechaViajeDesde ||
    fechaViajeHasta ||
    fechaInicioSelected ||
    filtroCerrado ||
    filtroAbierto

  // ?? CÁLCULOS FINANCIEROS
  const calcularCostoTotal = useMemo(() => {
    return editedIngredientes.reduce((total, servicio) => {
      const key = `${servicio.idMateriaPrima}-${selectedReceta?.destino}`
      const cantidad = Number.parseFloat(servicio.cantidad || 0)
      const costo = preciosCostos[key]?.costo || 0
      return total + (cantidad * costo)
    }, 0)
  }, [editedIngredientes, preciosCostos, selectedReceta?.destino])

  const calcularGanancia = useMemo(() => {
    return precioPaquete - calcularCostoTotal
  }, [precioPaquete, calcularCostoTotal])

  const calcularMargenGanancia = useMemo(() => {
    if (precioPaquete === 0) return 0
    return (calcularGanancia / precioPaquete) * 100
  }, [calcularGanancia, precioPaquete])

  const columnVisibilityModelEffective = useMemo(() => {
    const m = { ...columnVisibilityModel }
    m.monto = mostrarColumnaPrecio ? m.monto !== false : false
    m.precioPorPasajeros = mostrarColumnaPrecio ? m.precioPorPasajeros !== false : false
    m.acciones = tieneAlgunaAccion ? m.acciones !== false : false
    return m
  }, [columnVisibilityModel, mostrarColumnaPrecio, tieneAlgunaAccion])

  const handleColumnVisibilityModelChange = useCallback((newModel) => {
    setColumnVisibilityModel(newModel)
  }, [])

  const columns = [
    {
      field: "id",
      headerName: "ID",
      flex: 0.2,
      minWidth: 80,
      headerAlign: "center",
      hide: true,
      renderHeader: () => (
        <Typography variant="subtitle2" sx={{ color: "white", fontWeight: "bold" }}>
          ID
        </Typography>
      ),
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Chip label={params.value} size="small" color="primary" variant="outlined" sx={{ fontWeight: 600 }} />
        </Box>
      ),
    },
    {
      field: "fechaCreacion",
      headerName: "Fecha creación",
      flex: 0.3,
      minWidth: 120,
      hide: true,
      headerAlign: "center",
      align: "center",
      renderHeader: () => (
        <Typography variant="subtitle2" sx={{ color: "white", fontWeight: "bold" }}>
          Fecha creación
        </Typography>
      ),
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: "0.8rem" }}>
          {params.value ?? "N/A"}
        </Typography>
      ),
    },
    {
      field: "fechaViaje",
      headerName: "Fecha Viaje",
      flex: 0.35,
      minWidth: 110,
      headerAlign: "left",
      align: "left",
      valueGetter: (value, row) => row.fechaInicio ?? row.fechaCreacion ?? "N/A",
      renderHeader: () => (
        <Typography variant="subtitle2" sx={{ color: "white", fontWeight: "bold" }}>
          Fecha Viaje
        </Typography>
      ),
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <CalendarTodayIcon fontSize="small" sx={{ color: theme.palette.info.main }} />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: "codigoProductoTerminado",
      headerName: "Grupo",
      flex: 0.3,
      minWidth: 150,
      headerAlign: "center",
      align: "left",
      renderHeader: () => (
        <Typography variant="subtitle2" sx={{ color: "white", fontWeight: "bold" }}>
          Grupo
        </Typography>
      ),
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", width: "100%", justifyContent: "flex-start" }}>
          <Chip
            label={params.value}
            size="small"
            variant="outlined"
            sx={{
              fontWeight: 500,
              borderColor: "rgb(244, 147, 14)",
              color: "rgb(244, 147, 14)",
              borderRadius: 1,
              borderWidth: 1,
            }}
          />
        </Box>
      ),
    },
    {
      field: "destino",
      headerName: "Destino",
      flex: 0.6,
      minWidth: 150,
      headerAlign: "left",
      align: "left",
      renderHeader: () => (
        <Typography variant="subtitle2" sx={{ color: "white", fontWeight: "bold" }}>
          Destino
        </Typography>
      ),
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <LocationOnIcon fontSize="small" sx={{ color: theme.palette.secondary.main }} />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: "monto",
      headerName: "Precio",
      flex: 0.4,
      minWidth: 130,
      hide: !mostrarColumnaPrecio,
      headerAlign: "right",
      align: "right",
      valueGetter: (value, row) => Number(row?.monto ?? row?.precioPaquete ?? 0),
      sortComparator: (v1, v2) => v1 - v2,
      renderHeader: () => (
        <Typography variant="subtitle2" sx={{ color: "white", fontWeight: "bold", textAlign: "right", width: "100%" }}>
          Precio
        </Typography>
      ),
      renderCell: (params) => {
        const valor = Number(params.row?.monto ?? params.row?.precioPaquete ?? 0)
        const formatted = typeof valor === "number" && !isNaN(valor) ? `$${valor.toLocaleString("es-AR")}` : "$0,00"
        return (
          <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", width: "100%" }}>
            <Typography variant="body2" sx={{ fontWeight: "bold", color: theme.palette.primary.main, textAlign: "right" }}>
              {formatted}
            </Typography>
          </Box>
        )
      },
    },
    {
      field: "fechaFin",
      headerName: "Fecha Fin",
      flex: 0.4,
      minWidth: 120,
      hide: true,
      headerAlign: "center",
      renderHeader: () => (
        <Typography variant="subtitle2" sx={{ color: "white", fontWeight: "bold" }}>
          F. Fin
        </Typography>
      ),
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <CalendarTodayIcon fontSize="small" sx={{ color: theme.palette.warning.main }} />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: "butacas",
      headerName: "Butacas",
      flex: 0.25,
      minWidth: 95,
      headerAlign: "center",
      align: "center",
      renderHeader: () => (
        <Typography variant="subtitle2" sx={{ color: "white", fontWeight: "bold" }}>
          Butaca
        </Typography>
      ),
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color:
              Number(params.row?.pasajeros || 0) > Number(params.row?.butacas || 0)
                ? theme.palette.error.main
                : "inherit",
          }}
        >
          {Number(params.value || 0)}
        </Typography>
      ),
    },
    {
      field: "cerrado",
      headerName: "Estado",
      flex: 0.35,
      minWidth: 140,
      hide: true,
      headerAlign: "center",
      align: "center",
      renderHeader: () => (
        <Typography variant="subtitle2" sx={{ color: "white", fontWeight: "bold" }}>
          Estado
        </Typography>
      ),
      renderCell: (params) => {
        const idPaquete = params.row.idPaquete
        const cambio = cambiosPendientes.get(idPaquete)
        const esCerrado = cambio ? cambio.estadoNuevo : params.value === true
        const tieneCambios = cambiosPendientes.has(idPaquete)
        
        return (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
            <Tooltip 
              title={esCerrado ? "Paquete cerrado. Click para abrir" : "Paquete abierto. Click para cerrar"} 
              arrow
            >
              <Checkbox
                checked={esCerrado}
                onChange={() => handleToggleEstadoPaquete(params.row)}
                icon={<CheckBoxOutlineBlankIcon />}
                checkedIcon={<CheckBoxIcon />}
                sx={{
                  color: theme.palette.warning.main,
                  '&.Mui-checked': {
                    color: theme.palette.success.main,
                  },
                }}
              />
            </Tooltip>
            <Typography 
              variant="body2" 
              sx={{ 
                fontWeight: 600,
                color: esCerrado ? theme.palette.success.main : theme.palette.warning.main 
              }}
            >
              {esCerrado ? "Cerrado" : "Abierto"}
            </Typography>
            {tieneCambios && (
              <Tooltip title="Cambio pendiente" arrow>
                <Badge color="warning" variant="dot" />
              </Tooltip>
            )}
          </Box>
        )
      },
    },
    {
      field: "noches",
      headerName: "Noches",
      flex: 0.25,
      minWidth: 90,
      hide: true,
      headerAlign: "center",
      align: "center",
      renderHeader: () => (
        <Typography variant="subtitle2" sx={{ color: "white", fontWeight: "bold" }}>
          Noches
        </Typography>
      ),
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Chip 
            label={params.value || 0} 
            size="small" 
            color="secondary" 
            variant="outlined" 
            sx={{ fontWeight: 600 }} 
          />
        </Box>
      ),
    },
    {
      field: "totalIngredientes",
      headerName: "Servicios",
      flex: 0.3,
      minWidth: 120,
      headerAlign: "center",
      align: "center",
      renderHeader: () => (
        <Typography variant="subtitle2" sx={{ color: "white", fontWeight: "bold" }}>
          Servicios
        </Typography>
      ),
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Tooltip 
            title={
              params.value > 0 
                ? `Click para ver ${params.value} servicio${params.value > 1 ? 's' : ''}`
                : "No hay servicios asociados"
            }
          >
            <Box
              onClick={() => params.value > 0 && handleOpenModalServicios(params.row)}
              sx={{
                cursor: params.value > 0 ? "pointer" : "default",
                "&:hover": params.value > 0 ? {
                  transform: "scale(1.05)",
                  transition: "transform 0.2s ease"
                } : {}
              }}
            >
              <Badge badgeContent={params.value} color="info">
                <BallotIcon fontSize="small" sx={{ color: theme.palette.warning.main }} />
              </Badge>
            </Box>
          </Tooltip>
        </Box>
      ),
    },
    {
      field: "pasajeros",
      headerName: "Pasajeros Confirmados",
      flex: 0.3,
      minWidth: 150,
      headerAlign: "center",
      align: "center",
      renderHeader: () => (
        <Typography variant="subtitle2" sx={{ color: "white", fontWeight: "bold" }}>
          Pasajeros Confirmados
        </Typography>
      ),
      renderCell: (params) => {
        const haySobrecapacidad =
          Number(params.row?.pasajeros || 0) > Number(params.row?.butacas || 0)

        return (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Tooltip
              title={
                params.value > 0
                  ? `Click para ver ${params.value} pasajero${params.value > 1 ? "s" : ""}`
                  : "No hay pasajeros confirmados"
              }
            >
              <Box
                onClick={() => params.value > 0 && handleOpenModalPasajerosConfirmados(params.row)}
                sx={{
                  cursor: params.value > 0 ? "pointer" : "default",
                  "&:hover": params.value > 0
                    ? { transform: "scale(1.05)", transition: "transform 0.2s ease" }
                    : {},
                }}
              >
                <Badge badgeContent={params.value || 0} color={haySobrecapacidad ? "error" : "info"}>
                  <GroupsIcon
                    fontSize="small"
                    sx={{ color: haySobrecapacidad ? theme.palette.error.main : theme.palette.info.main }}
                  />
                </Badge>
              </Box>
            </Tooltip>
          </Box>
        )
      },
    },
      {
        field: "vendedor",
        headerName: "Vendedor",
        flex: 0.55,
        minWidth: 180,
        headerAlign: "center",
        align: "center",
        renderHeader: () => (
          <Typography variant="subtitle2" sx={{ color: "white", fontWeight: "bold" }}>
            Vendedor
          </Typography>
        ),
        renderCell: (params) => {
          const cantidadVendedoresBom = Number(params.row?.cantidadVendedoresBom ?? 0)
          const primerVendedorBom = params.row?.primerVendedorBom ?? null
          if (cantidadVendedoresBom > 0) {
            console.log("[TablaReceta] renderCell Vendedor:", {
              id: params.row?.id ?? params.row?.idPaquete,
              cantidadVendedoresBom,
              primerVendedorBom,
              rowKeys: Object.keys(params.row || {}),
            })
          }

          // La columna muestra proveedores con flag vendedor en servicios BOM (esVendedor=true)
          // 1. Sin vendedores en BOM → "-"
          if (cantidadVendedoresBom <= 0) {
            return (
              <Typography variant="body2" color="text.secondary">
                -
              </Typography>
            )
          }

          // 2. Un solo vendedor BOM → nombre del proveedor (descripcion + apellido)
          if (cantidadVendedoresBom === 1) {
            const nombreUnico = primerVendedorBom || "-"
            return (
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {nombreUnico}
              </Typography>
            )
          }

          // 3. Varios vendedores BOM → PersonIcon + Badge + click para abrir modal
          const puedeAbrirModal = !!params.row?.idProductoTerminado

          return (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Tooltip
                title={puedeAbrirModal ? `Click para ver ${cantidadVendedoresBom} vendedores` : "No hay vendedor asociado"}
              >
                <Box
                  onClick={() => puedeAbrirModal && handleOpenModalVendedores(params.row)}
                  sx={{
                    cursor: puedeAbrirModal ? "pointer" : "default",
                    "&:hover": puedeAbrirModal
                      ? { transform: "scale(1.05)", transition: "transform 0.2s ease" }
                      : {},
                  }}
                >
                  <Badge badgeContent={cantidadVendedoresBom} color="info">
                    <PersonIcon fontSize="small" sx={{ color: theme.palette.info.main }} />
                  </Badge>
                </Box>
              </Tooltip>
            </Box>
          )
        },
      },
      {
        field: "observaciones",
        headerName: "Observaciones",
        flex: 0.8,
        minWidth: 220,
        headerAlign: "left",
        align: "left",
        renderHeader: () => (
          <Typography variant="subtitle2" sx={{ color: "white", fontWeight: "bold", textAlign: "left", width: "100%" }}>
            Observaciones
          </Typography>
        ),
        renderCell: (params) => (
          <Box sx={{ display: "flex", justifyContent: "flex-start", alignItems: "flex-start", width: "100%", textAlign: "left" }}>
            <Typography variant="body2" sx={{ whiteSpace: "normal", lineHeight: 1.25, textAlign: "left" }}>
              {params.value || "-"}
            </Typography>
          </Box>
        ),
      },
      
    {
      field: "precioPorPasajeros",
      headerName: "Precio Total",
      flex: 0.4,
      minWidth: 120,
      hide: !mostrarColumnaPrecio,
      headerAlign: "right",
      align: "right",
      renderHeader: () => (
        <Typography variant="subtitle2" sx={{ color: "white", fontWeight: "bold" }}>
          Precio Total
        </Typography>
      ),
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 1,
          }}
        >
          <MonetizationOnIcon fontSize="small" sx={{ color: theme.palette.success.main }} />
          <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.success.main }}>
            {formatearMoneda(params.value || 0)}
          </Typography>
        </Box>
      ),
    },
    {
      field: "costoTotalServicios",
      headerName: "Costo Total",
      flex: 0.4,
      minWidth: 120,
      hide: true,
      headerAlign: "right",
      align: "right",
      renderHeader: () => (
        <Typography variant="subtitle2" sx={{ color: "white", fontWeight: "bold" }}>
          Costo Total
        </Typography>
      ),
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 1,
          }}
        >
          <AttachMoneyIcon fontSize="small" sx={{ color: theme.palette.warning.main }} />
          <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.warning.main }}>
            {formatearMoneda(params.value)}
          </Typography>
        </Box>
      ),
    },
    {
      field: "ganancia",
      headerName: "Ganancia",
      flex: 0.4,
      minWidth: 120,
      hide: true,
      headerAlign: "right",
      align: "right",
      renderHeader: () => (
        <Typography variant="subtitle2" sx={{ color: "white", fontWeight: "bold" }}>
          Ganancia
        </Typography>
      ),
      renderCell: (params) => {
        const ganancia = params.value || 0
        const color = ganancia >= 0 ? theme.palette.info.main : theme.palette.error.main
        return (
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 1,
            }}
          >
            <AccountBalanceWalletIcon fontSize="small" sx={{ color }} />
            <Typography variant="body2" sx={{ fontWeight: 600, color }}>
              {formatearMoneda(ganancia)}
            </Typography>
          </Box>
        )
      },
    },
    {
      field: "porcentajeGanancia",
      headerName: "% Ganancia",
      flex: 0.4,
      minWidth: 120,
      hide: true,
      headerAlign: "center",
      renderHeader: () => (
        <Typography variant="subtitle2" sx={{ color: "white", fontWeight: "bold" }}>
          % Ganancia
        </Typography>
      ),
      renderCell: (params) => {
        const porcentaje = params.value || 0
        const colorGradiente = generarColorGradiente(porcentaje)
        return (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 1,
              backgroundColor: colorGradiente,
              borderRadius: 2,
              px: 1.5,
              py: 0.5,
              minWidth: 80,
            }}
          >
            <PercentIcon fontSize="small" sx={{ color: "white" }} />
            <Typography 
              variant="body2" 
              sx={{ 
                fontWeight: 700, 
                color: "white",
                textShadow: "0 1px 2px rgba(0,0,0,0.3)"
              }}
            >
              {porcentaje.toFixed(1)}%
            </Typography>
          </Box>
        )
      },
    },
    {
      field: "fechaActualizacion",
      headerName: "Fecha Actualización",
      flex: 0.3,
      minWidth: 130,
      hide: true,
      headerAlign: "center",
      renderHeader: () => (
        <Typography variant="subtitle2" sx={{ color: "white", fontWeight: "bold" }}>
          F. Actualización
        </Typography>
      ),
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: "0.8rem" }}>
            {params.value}
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.7rem" }}>
            {params.row.horaActualizacion}
          </Typography>
        </Box>
      ),
    },
    {
      field: "acciones",
      headerName: "Acciones",
      flex: 0.3,
      minWidth: 240,
      hide: !tieneAlgunaAccion,
      headerAlign: "center",
      sortable: false,
      renderHeader: () => (
        <Typography variant="subtitle2" sx={{ color: "white", fontWeight: "bold" }}>
          Acciones
        </Typography>
      ),
      renderCell: (params) => (
        <Box sx={{ display: "flex", justifyContent: "center", gap: 1, width: "100%", overflow: "visible" }}>
          {mostrarBtnEditarGrupo && (
            <Tooltip title="Editar Grupo">
              <IconButton
                size="small"
                onClick={() => handleOpenModalEdicion(params.row)}
                sx={{
                  bgcolor: alpha(theme.palette.secondary.main, 0.1),
                  "&:hover": {
                    bgcolor: alpha(theme.palette.secondary.main, 0.2),
                    transform: "scale(1.1)",
                  },
                  transition: "all 0.2s ease",
                }}
              >
                <EditIcon fontSize="small" sx={{ color: theme.palette.secondary.main }} />
              </IconButton>
            </Tooltip>
          )}
          {mostrarBtnEditarPasajeros && (
            <Tooltip title="Editar Pasajeros">
              <IconButton
                size="small"
                onClick={() => handleOpenModalPasajeros(params.row)}
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  "&:hover": {
                    bgcolor: alpha(theme.palette.primary.main, 0.2),
                    transform: "scale(1.1)",
                  },
                  transition: "all 0.2s ease",
                }}
              >
                <GroupsIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
              </IconButton>
            </Tooltip>
          )}
          {mostrarBtnDescargarPdf && (
            <Tooltip title="Lista de pasajeros para compartir">
              <IconButton
                size="small"
                onClick={() => generateClientesPDF(params.row)}
                sx={{
                  bgcolor: alpha("#1976d2", 0.1),
                  "&:hover": {
                    bgcolor: alpha("#1976d2", 0.2),
                    transform: "scale(1.1)",
                  },
                  transition: "all 0.2s ease",
                }}
              >
                <PictureAsPdfIcon fontSize="small" sx={{ color: "#1976d2 !important" }} />
              </IconButton>
            </Tooltip>
          )}
          {mostrarBtnExcel && (
            <Tooltip title="Exportar pasajeros a Excel">
              <IconButton
                size="small"
                onClick={() => exportarClientesExcel(params.row)}
                sx={{
                  bgcolor: alpha("#107C41", 0.1),
                  "&:hover": {
                    bgcolor: alpha("#107C41", 0.2),
                    transform: "scale(1.1)",
                  },
                  transition: "all 0.2s ease",
                }}
              >
                <GridOnIcon fontSize="small" sx={{ color: "#107C41" }} />
              </IconButton>
            </Tooltip>
          )}
          {mostrarBtnCuponera && usaCuponera && (
            <Tooltip title="Imprimir cuponera">
              <IconButton
                size="small"
                onClick={() => handleOpenModalCuponeraDesdeFila(params.row)}
                sx={{
                  bgcolor: alpha(theme.palette.info.main, 0.1),
                  "&:hover": {
                    bgcolor: alpha(theme.palette.info.main, 0.2),
                    transform: "scale(1.1)",
                  },
                  transition: "all 0.2s ease",
                }}
              >
                <PrintIcon fontSize="small" sx={{ color: theme.palette.info.main }} />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Eliminar Grupo">
            <IconButton
              size="small"
              onClick={() => handleEliminarGrupo(params.row)}
              sx={{
                bgcolor: alpha("#d33", 0.1),
                "&:hover": {
                  bgcolor: alpha("#d33", 0.2),
                  transform: "scale(1.1)",
                },
                transition: "all 0.2s ease",
              }}
            >
              <DeleteIcon fontSize="small" sx={{ color: "#d33" }} />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ]

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100vh",
          width: "100%",
          overflow: "hidden",
          position: "relative",
          backgroundColor: "#f8f9fa",
          boxSizing: "border-box",
        }}
      >
        {/* Barra compacta para Laptop/Mobile: botón de Filtros + acciones clave */}
        {isLaptopDown && (
          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
              px: 1,
              pt: 1,
            }}
          >
            <Button
              variant="outlined"
              size="small"
              startIcon={<FilterListIcon />}
              onClick={() => setFiltersOpen((v) => !v)}
              sx={{ textTransform: "none" }}
            >
              {filtersOpen ? "Ocultar filtros" : "Mostrar filtros"}
            </Button>

            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="contained"
                size="small"
                onClick={aplicarFiltros}
                startIcon={<SearchIcon />}
                sx={{
                  backgroundColor: "#5b5b5f",
                  textTransform: "none",
                  color: "white",
                  "&:hover": { backgroundColor: alpha("#5b5b5f", 0.9) },
                }}
              >
                Buscar
              </Button>
              {mostrarBtnExportarTablaPdf && (
                <Tooltip title="Exportar tabla a PDF">
                  <span>
                    <IconButton
                      onClick={handleExportarPdf}
                      disabled={loading}
                      size="small"
                      sx={{
                        color: "#d32f2f",
                        "&:hover": { color: "#b71c1c" },
                        "&.Mui-disabled": { color: "rgba(0, 0, 0, 0.26)" },
                      }}
                    >
                      <PictureAsPdfIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              <Tooltip title="Crear nuevo grupo">
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleOpenCrearPaqueteModal}
                  sx={{
                    textTransform: "none",
                    backgroundColor: "#5b5b5f",
                    "&:hover": { backgroundColor: alpha("#5b5b5f", 0.8) },
                  }}
                >
                  Crear
                </Button>
              </Tooltip>
            </Box>
          </Box>
        )}

        {/* Contenedor de filtros (siempre visible en Desktop; colapsable en Laptop/Mobile) */}
        <Collapse in={filtersOpen || !isLaptopDown} timeout={250} unmountOnExit>
          <Box sx={{ p: 1, flexShrink: 0 }}>
            <Paper sx={{ 
              p: 2, 
              borderRadius: 2, 
              mb: 2, 
              backgroundColor: alpha(theme.palette.background.paper, 0.9),
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            }}>
              {/* Encabezado en una sola fila (desktop). En pantallas chicas puede envolver. */}
              <Grid container spacing={1.5} alignItems="center" wrap="wrap">
                <Grid item xs={12} md={2}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      value={fechaViajeDesde}
                      onChange={(newValue) => setFechaViajeDesde(newValue)}
                      format="DD/MM/YYYY"
                      label="Viaje Desde"
                      slotProps={{
                        textField: { size: "small", fullWidth: true },
                      }}
                    />
                  </LocalizationProvider>
                </Grid>

                <Grid item xs={12} md={2}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      value={fechaViajeHasta}
                      onChange={(newValue) => setFechaViajeHasta(newValue)}
                      format="DD/MM/YYYY"
                      label="Viaje Hasta"
                      slotProps={{
                        textField: { size: "small", fullWidth: true },
                      }}
                    />
                  </LocalizationProvider>
                </Grid>

                <Grid item xs={12} md={2}>
                  <Autocomplete
                    size="small"
                    fullWidth
                    disablePortal
                    options={(itemTresLetras || []).map((it) => ({
                      label: generateRecetaLabel(it),
                      descripcion: it.descripcion,
                      codigo: it.codigo,
                      id: it.id,
                      codigoScanner: it.codigoScanner,
                    }))}
                    value={recetaSelected}
                    onChange={handleRecetaChange}
                    onInputChange={(event, newInputValue) => setSearchTextReceta(newInputValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Buscar Grupo"
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <SearchIcon color="action" sx={{ ml: 0.5, mr: 0 }} fontSize="small" />
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={2}>
                  <Autocomplete
                    size="small"
                    fullWidth
                    disablePortal
                    options={(ubicaciones || [])
                      .filter((ubicacion) => ubicacion.esDestino === true)
                      .map((ubicacion) => ({
                        label: ubicacion.descripcion,
                        id: ubicacion.id,
                        descripcion: ubicacion.descripcion,
                      }))}
                    value={destinoSelected}
                    onChange={handleDestinoChange}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Buscar Destino"
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <LocationOnIcon color="action" sx={{ ml: 0.5, mr: 0 }} fontSize="small" />
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={2}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Buscar Vendedor"
                    value={filtroVendedor}
                    onChange={(event) => setFiltroVendedor(event.target.value)}
                  />
                </Grid>

                <Grid item xs={12} md={2}>
                  <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", alignItems: "center" }}>
                    <Button
                      variant="contained"
                      onClick={aplicarFiltros}
                      startIcon={<SearchIcon />}
                      sx={{
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 600,
                        backgroundColor: "#5b5b5f",
                        px: 2,
                        "&:hover": {
                          backgroundColor: alpha("#5b5b5f", 0.9),
                        },
                      }}
                    >
                      Buscar
                    </Button>

                    <Tooltip title="Limpiar filtros">
                      <span>
                        <IconButton
                          onClick={limpiarFiltros}
                          disabled={!hayFiltrosActivos}
                          size="small"
                          sx={{
                            borderRadius: 2,
                            border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                          }}
                        >
                          <FilterListIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>

                    <Tooltip title="Crear nuevo grupo">
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleOpenCrearPaqueteModal}
                        sx={{
                          textTransform: "none",
                          backgroundColor: "#5b5b5f",
                          borderRadius: 2,
                          "&:hover": {
                            backgroundColor: alpha("#5b5b5f", 0.8),
                          },
                        }}
                      >
                        Crear
                      </Button>
                    </Tooltip>

                    {mostrarBtnExportarTablaPdf && (
                      <Tooltip title="Exportar tabla a PDF">
                        <span>
                          <IconButton
                            onClick={handleExportarPdf}
                            disabled={loading}
                            size="small"
                            sx={{
                              color: "#d32f2f",
                              "&:hover": { color: "#b71c1c" },
                              "&.Mui-disabled": { color: "rgba(0, 0, 0, 0.26)" },
                            }}
                          >
                            <PictureAsPdfIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </Box>
                </Grid>
              </Grid>
        </Paper>

        {/* ?? Checkboxes de filtro por estado (Cerrado/Abierto) */}
        <Box sx={{ px: 1, pb: 1 }}>
          <Paper sx={{ 
            p: 1.5, 
            borderRadius: 2, 
            backgroundColor: alpha(theme.palette.background.paper, 0.9),
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          }}>
            <Box sx={{ display: "flex", gap: 3, alignItems: "center", flexWrap: "wrap" }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.secondary }}>
                Filtrar por estado:
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filtroCerrado}
                    onChange={handleFiltroCerradoChange}
                    size="small"
                    sx={{
                      color: theme.palette.success.main,
                      "&.Mui-checked": {
                        color: theme.palette.success.main,
                      },
                    }}
                  />
                }
                label={
                  <Chip
                    label="Cerrado"
                    size="small"
                    color="success"
                    variant={filtroCerrado ? "filled" : "outlined"}
                    sx={{ fontWeight: 600 }}
                  />
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filtroAbierto}
                    onChange={handleFiltroAbiertoChange}
                    size="small"
                    sx={{
                      color: theme.palette.info.main,
                      "&.Mui-checked": {
                        color: theme.palette.info.main,
                      },
                    }}
                  />
                }
                label={
                  <Chip
                    label="Abierto"
                    size="small"
                    color="info"
                    variant={filtroAbierto ? "filled" : "outlined"}
                    sx={{ fontWeight: 600 }}
                  />
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filtroCancelado}
                    onChange={handleFiltroCanceladoChange}
                    size="small"
                    sx={{
                      color: theme.palette.error.main,
                      "&.Mui-checked": {
                        color: theme.palette.error.main,
                      },
                    }}
                  />
                }
                label={
                  <Chip
                    label="Cancelado"
                    size="small"
                    color="error"
                    variant={filtroCancelado ? "filled" : "outlined"}
                    sx={{ fontWeight: 600 }}
                  />
                }
              />
            </Box>
          </Paper>
        </Box>
        </Box>
        </Collapse>

        {/* Tabla */}
        <Paper
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            m: 1,
            mt: 0,
            borderRadius: 3,
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
            position: "relative",
          }}
        >
          <DataGrid
            key={isNavbarCollapsed ? "collapsed" : "expanded"}
            rows={filteredRecetas}
            columns={columns}
            getRowId={(row) => row.id}
            getRowClassName={getRowClassName}
            pageSize={25}
            rowsPerPageOptions={[10, 25, 50]}
            disableSelectionOnClick
            loading={loading}
            density="compact"
            sortingMode="server"
            sortingOrder={["desc", "asc"]}
            sortModel={sortModel}
            onSortModelChange={handleSortModelChange}
            getRowHeight={() => 34}
            columnHeaderHeight={40}
            columnVisibilityModel={columnVisibilityModelEffective}
            onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
            initialState={{
              columns: {
                orderedFields: [
                  "fechaViaje",
                  "codigoProductoTerminado",
                  "destino",
                  "monto",
                  "totalIngredientes",
                  "butacas",
                  "pasajeros",
                  "vendedor",
                  "observaciones",
                  "acciones",
                  "id",
                  "fechaInicio",
                  "fechaFin",
                  "cerrado",
                  "noches",
                  "precioPorPasajeros",
                  "costoTotalServicios",
                  "ganancia",
                  "porcentajeGanancia",
                  "fechaActualizacion",
                  "fechaCreacion",
                ],
              },
              pinnedColumns: {
                left: ["fechaViaje", "codigoProductoTerminado", "destino"],
              },
            }}
            slots={{
              toolbar: GridToolbar,
            }}
            slotProps={{
              toolbar: {
                showQuickFilter: false,
                showColumnsButton: true,
              },
            }}
            className="tablaRecetaDataGrid"
            sx={{
              width: "100%",
              border: 0,
              height: "100%",
              minWidth: 0,
              "& .MuiDataGrid-toolbarContainer": {
                display: "flex !important",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 0.5,
                py: 0.5,
                px: 0.5,
              },
              "& .MuiDataGrid-footerContainer": {
                display: "none !important",
              },
              "& .MuiDataGrid-columnHeaders": {
                //MANEJA CSS HEADER
                backgroundColor: "#5b5b5f",
                color: "white",
                borderBottom: "2px solid #333",
                height: "40px !important",
                minHeight: "40px !important",
                maxHeight: "40px !important",
              },
              "& .MuiDataGrid-columnHeader": {
                //MANEJA CSS HEADER
                height: "40px !important",
                minHeight: "40px !important",
                maxHeight: "40px !important",
              },
              // ?? Estilos para columnas pinned (sticky nativo de MUI)
              "& .MuiDataGrid-pinnedColumnHeaders": {
                backgroundColor: "#5b5b5f !important",
                boxShadow: "2px 0 5px rgba(0,0,0,0.1)",
              },
              "& .MuiDataGrid-pinnedColumns": {
                boxShadow: "2px 0 5px rgba(0,0,0,0.1)",
              },
              "& .MuiDataGrid-pinnedColumns--left": {
                backgroundColor: "transparent",
              },
              "& .MuiDataGrid-cell.MuiDataGrid-cell--pinnedLeft": {
                backgroundColor: "#ffffff !important",
              },
              "& .MuiDataGrid-row:nth-of-type(even) .MuiDataGrid-cell.MuiDataGrid-cell--pinnedLeft": {
                backgroundColor: "#F7F7F7 !important",
              },
              "& .MuiDataGrid-row:hover .MuiDataGrid-cell.MuiDataGrid-cell--pinnedLeft": {
                backgroundColor: "#E3EAFD !important",
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                //MANEJA CSS HEADER
                color: "white !important",
                lineHeight: "40px !important",
                fontSize: "0.8rem",
                fontWeight: 700,
              },
              "& .MuiDataGrid-columnHeaderTitle .MuiDataGrid-menuIconButton": {
                color: "white !important",
              },
              "& .MuiDataGrid-menuIconButton": {
                color: "rgb(244, 177, 14) !important",
              },
              "& .MuiInputBase-root": {
                color: "rgb(244, 177, 14) !important",
              },
              "& .MuiTablePagination-root": {
                color: "black !important",
              },
              "& .MuiSelect-icon": {
                color: "rgb(244, 177, 14) !important",
              },
              "& .MuiDataGrid-cell": {
                borderBottom: "1px solid #ccc",
                display: "flex",
                alignItems: "center",
                px: 1,
                py: 0,
                fontSize: "0.82rem",
              },
              "& .MuiDataGrid-cellContent": {
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              },
              "& .MuiDataGrid-cell[data-field='acciones']": {
                overflow: "visible !important",
                "& .MuiDataGrid-cellContent": {
                  overflow: "visible !important",
                  textOverflow: "clip",
                  whiteSpace: "normal",
                },
              },
              "& .MuiDataGrid-row": {
                "&:nth-of-type(even)": {
                  backgroundColor: "#F7F7F7",
                },
                "&:hover": {
                  backgroundColor: "#E3EAFD !important",
                  transition: "background 0.3s ease",
                },
              },
              "& .fila-cambio-pendiente": {
                backgroundColor: alpha(theme.palette.warning.main, 0.1),
                "&:hover": {
                  backgroundColor: alpha(theme.palette.warning.main, 0.15),
                },
              },
              "& .fila-sobrecapacidad": {
                backgroundColor: `${alpha(theme.palette.error.main, 0.08)} !important`,
                "& .MuiDataGrid-cell": {
                  backgroundColor: `${alpha(theme.palette.error.main, 0.08)} !important`,
                },
                "& .MuiDataGrid-cell.MuiDataGrid-cell--pinnedLeft": {
                  backgroundColor: `${alpha(theme.palette.error.main, 0.08)} !important`,
                },
                "&:hover": {
                  backgroundColor: `${alpha(theme.palette.error.main, 0.12)} !important`,
                },
                "&:hover .MuiDataGrid-cell": {
                  backgroundColor: `${alpha(theme.palette.error.main, 0.12)} !important`,
                },
                "&:hover .MuiDataGrid-cell.MuiDataGrid-cell--pinnedLeft": {
                  backgroundColor: `${alpha(theme.palette.error.main, 0.12)} !important`,
                },
              },
              "& .row-devolucion": {
                backgroundColor: alpha("#9e9e9e !important", 0.05),
                "&:hover": {
                  backgroundColor: alpha("#9e9e9e !important", 0.1),
                },
              },
              "& .MuiDataGrid-virtualScroller": {
                lineHeight: "none !important",
                height: "10px !imporant",
              },
              "& .MuiDataGrid-footerContainer": {
                position: "sticky",
                bottom: 0,
                backgroundColor: "#ffffff",
                borderTop: "1px solid #ddd",
                zIndex: 10,
              },
              "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
                width: "2px",
              },
              "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb": {
                backgroundColor: "#888",
                borderRadius: "10px",
              },
            }}
          />
        </Paper>

        {/* Footer (paginación) */}
        <Box sx={{ flexShrink: 0, p: 1 }}>
            <CustomPagination
            currentPage={page}
            totalPages={totalPages}
            totalCount={totalCount}
            montoTotalTransacciones={montoTotalTransacciones}
            vieneDe={"tablaRecetas"}
            onPageChange={handlePageChange}
            totalPrecioOriginal={totalPrecioOriginal}
            totalPasajerosConfirmados={totalPasajerosConfirmados}
            loadingTotales={loadingTotales}
            mostrarPrecioOriginal={mostrarPrecioOriginalFooter}
          />
        </Box>

        {/* Botón Guardar Estados */}
        {cambiosPendientes.size > 0 && (
          <Box 
            sx={{ 
              display: "flex", 
              justifyContent: "center", 
              alignItems: "center",
              gap: 2,
              mt: 2,
              mb: 2
            }}
          >
            <Chip
              label={`${cambiosPendientes.size} cambio(s) pendiente(s)`}
              color="warning"
              size="small"
              sx={{ fontWeight: 600 }}
            />
            <Button
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              onClick={handleGuardarEstados}
              sx={{
                bgcolor: theme.palette.primary.main,
                "&:hover": {
                  bgcolor: alpha(theme.palette.primary.main, 0.8),
                },
                fontWeight: 600,
                px: 3
              }}
            >
              Guardar Estados
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<ClearIcon />}
              onClick={() => {
                Swal.fire({
                  title: '¿Descartar cambios?',
                  text: 'Se perderán todos los cambios pendientes sin guardar',
                  icon: 'warning',
                  showCancelButton: true,
                  confirmButtonText: 'Sí, descartar',
                  cancelButtonText: 'Cancelar'
                }).then((result) => {
                  if (result.isConfirmed) {
                    setCambiosPendientes(new Map())
                    Swal.fire('Cambios descartados', '', 'success')
                  }
                })
              }}
              sx={{ fontWeight: 600 }}
            >
              Descartar Cambios
            </Button>
          </Box>
        )}

        {/* Modal mejorado sutilmente */}
        <Modal open={openModal} onClose={handleCloseModal} aria-labelledby="modal-receta-titulo">
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: { xs: "95%", md: "85%", lg: "75%" },
              maxWidth: 1000,
              maxHeight: "90vh",
              bgcolor: "background.paper",
              boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
              borderRadius: 3,
              overflow: "hidden",
            }}
          >
            {/* Header del modal mejorado */}
            <Box
              sx={{
                p: 3,
                backgroundColor: "#5b5b5f",
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    bgcolor: "rgba(255,255,255,0.1)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <GroupsIcon sx={{ fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Detalle de Grupo - Servicios
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    {selectedReceta?.descripcionProductoTerminado}
                  </Typography>
                </Box>
              </Box>
              <IconButton
                onClick={handleCloseModal}
                sx={{
                  color: "white",
                  bgcolor: "rgba(255,255,255,0.1)",
                  "&:hover": {
                    bgcolor: "rgba(255,255,255,0.2)",
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Contenido del modal */}
            <Box sx={{ p: 3, maxHeight: "calc(90vh - 120px)", overflowY: "auto" }}>
              {selectedReceta && (
                <>
                  {/* Indicador de carga */}
                  {cargandoDatosModal && (
                    <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                      <CircularProgress size={24} />
                      <Typography variant="body2" sx={{ ml: 1 }}>
                        Cargando datos financieros...
                      </Typography>
                    </Box>
                  )}

                  {/* Información general mejorada */}
                  <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid item xs={12} md={2.4}>
                      <Paper
                        sx={{
                          p: 2.5,
                          textAlign: "center",
                          backgroundColor: alpha(theme.palette.primary.main, 0.05),
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                        }}
                      >
                        <BallotIcon sx={{ fontSize: 32, color: theme.palette.primary.main, mb: 1 }} />
                        <Typography variant="h5" color="primary" sx={{ fontWeight: 700, mb: 0.5 }}>
                          {selectedReceta.totalIngredientes}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total Servicios
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={2.4}>
                      <Paper
                        sx={{
                          p: 2.5,
                          textAlign: "center",
                          backgroundColor: alpha(theme.palette.success.main, 0.05),
                          border: `1px solid ${alpha(theme.palette.success.main, 0.1)}`,
                        }}
                      >
                        <MonetizationOnIcon sx={{ fontSize: 32, color: theme.palette.success.main, mb: 1 }} />
                        <Typography variant="h5" color="success.main" sx={{ fontWeight: 700, mb: 0.5 }}>
                          {formatearMoneda(precioPaquete)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Precio del Paquete
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={2.4}>
                      <Paper
                        sx={{
                          p: 2.5,
                          textAlign: "center",
                          backgroundColor: alpha(theme.palette.warning.main, 0.05),
                          border: `1px solid ${alpha(theme.palette.warning.main, 0.1)}`,
                        }}
                      >
                        <AttachMoneyIcon sx={{ fontSize: 32, color: theme.palette.warning.main, mb: 1 }} />
                        <Typography variant="h5" color="warning.main" sx={{ fontWeight: 700, mb: 0.5 }}>
                          {formatearMoneda(calcularCostoTotal)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Costo Total
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={2.4}>
                      <Paper
                        sx={{
                          p: 2.5,
                          textAlign: "center",
                          backgroundColor: alpha(theme.palette.info.main, 0.05),
                          border: `1px solid ${alpha(theme.palette.info.main, 0.1)}`,
                        }}
                      >
                        <AccountBalanceWalletIcon sx={{ fontSize: 32, color: theme.palette.info.main, mb: 1 }} />
                        <Typography variant="h5" color="info.main" sx={{ fontWeight: 700, mb: 0.5 }}>
                          {formatearMoneda(calcularGanancia)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Ganancia
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={2.4}>
                      <Paper
                        sx={{
                          p: 2.5,
                          textAlign: "center",
                          backgroundColor: alpha(theme.palette.secondary.main, 0.05),
                          border: `1px solid ${alpha(theme.palette.secondary.main, 0.1)}`,
                        }}
                      >
                        <PercentIcon sx={{ fontSize: 32, color: theme.palette.secondary.main, mb: 1 }} />
                        <Typography variant="h5" color="secondary.main" sx={{ fontWeight: 700, mb: 0.5 }}>
                          {calcularMargenGanancia.toFixed(1)}%
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Margen
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>

                  {/* Tabla de ingredientes */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                      <EditIcon color="primary" />
                      Servicios del Grupo
                    </Typography>

                    <TableContainer
                      component={Paper}
                      sx={{
                        borderRadius: 2,
                        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      }}
                    >
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.1) }}>
                            <TableCell sx={{ fontWeight: 600 }}>Código</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Servicio</TableCell>
                            <TableCell sx={{ fontWeight: 600, textAlign: "center" }}>Cantidad</TableCell>
                            <TableCell sx={{ fontWeight: 600, textAlign: "center" }}>Costo Unit.</TableCell>
                            <TableCell sx={{ fontWeight: 600, textAlign: "center" }}>Subtotal</TableCell>
                            <TableCell sx={{ fontWeight: 600, textAlign: "center" }}>Estado</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {editedIngredientes.map((ingrediente, index) => {
                            const key = `${ingrediente.idMateriaPrima}-${selectedReceta?.destino}`
                            const costoUnitario = preciosCostos[key]?.costo || 0
                            const subtotal = Number.parseFloat(ingrediente.cantidad || 0) * costoUnitario
                            
                            return (
                              <TableRow
                                key={ingrediente.idMateriaPrima}
                                sx={{
                                  "&:hover": {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.02),
                                  },
                                  "&:nth-of-type(even)": {
                                    backgroundColor: alpha(theme.palette.grey[50], 0.5),
                                  },
                                }}
                              >
                                <TableCell>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontFamily: "monospace",
                                      fontWeight: 500,
                                      bgcolor: alpha(theme.palette.secondary.main, 0.1),
                                      px: 1,
                                      py: 0.5,
                                      borderRadius: 1,
                                      display: "inline-block",
                                    }}
                                  >
                                    {ingrediente.materiaPrima?.codigo || "N/A"}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <BallotIcon fontSize="small" sx={{ color: theme.palette.info.main }} />
                                    <Box>
                                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        {ingrediente.materiaPrima?.descripcion || "Sin descripción"}
                                      </Typography>
                                      {generarAtributosSubtexto(ingrediente.materiaPrima) && (
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          sx={{
                                            display: "block", 
                                            mt: 0.5, 
                                            fontStyle: "italic",
                                            opacity: 0.7,
                                          }}
                                        >
                                          {generarAtributosSubtexto(ingrediente.materiaPrima)}
                                        </Typography>
                                      )}
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell sx={{ textAlign: "center" }}>
                                  <TextField
                                    size="small"
                                    type="number"
                                    value={ingrediente.cantidad === 0 ? "" : ingrediente.cantidad}
                                    onChange={(e) => {
                                      const value = e.target.value
                                      if (value === "") {
                                        handleCantidadChange(index, 0)
                                      } else {
                                        handleCantidadChange(index, value)
                                      }
                                    }}
                                    inputProps={{
                                      step: "0.01",
                                      min: "0",
                                    }}
                                    sx={{
                                      width: 130,
                                      "& .MuiOutlinedInput-root": {
                                        borderRadius: 2,
                                      },
                                    }}
                                  />
                                </TableCell>
                                <TableCell sx={{ textAlign: "center" }}>
                                  <Typography variant="body2" color="warning.main" sx={{ fontWeight: 600 }}>
                                    {formatearMoneda(costoUnitario)}
                                  </Typography>
                                </TableCell>
                                <TableCell sx={{ textAlign: "center" }}>
                                  <Typography variant="body2" color="error.main" sx={{ fontWeight: 600 }}>
                                    {formatearMoneda(subtotal)}
                                  </Typography>
                                </TableCell>
                                <TableCell sx={{ textAlign: "center" }}>
                                  <Chip
                                    label={ingrediente.eliminado ? "Eliminado" : "Activo"}
                                    size="small"
                                    color={ingrediente.eliminado ? "error" : "success"}
                                    variant={ingrediente.eliminado ? "filled" : "outlined"}
                                    sx={{ fontWeight: 600 }}
                                  />
                                </TableCell>
                              </TableRow>
                            )
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>

                  {/* Resumen financiero */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                      <TrendingUpIcon color="primary" />
                      Resumen Financiero
                    </Typography>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <Paper
                          sx={{
                            p: 2,
                            textAlign: "center",
                            backgroundColor: alpha(theme.palette.success.main, 0.05),
                            border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                          }}
                        >
                          <Typography variant="h6" color="success.main" sx={{ fontWeight: 700 }}>
                            {formatearMoneda(precioPaquete)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Precio del Paquete
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Paper
                          sx={{
                            p: 2,
                            textAlign: "center",
                            backgroundColor: alpha(theme.palette.warning.main, 0.05),
                            border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                          }}
                        >
                          <Typography variant="h6" color="warning.main" sx={{ fontWeight: 700 }}>
                            {formatearMoneda(calcularCostoTotal)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Costo Total
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Paper
                          sx={{
                            p: 2,
                            textAlign: "center",
                            backgroundColor: alpha(theme.palette.info.main, 0.05),
                            border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                          }}
                        >
                          <Typography variant="h6" color="info.main" sx={{ fontWeight: 700 }}>
                            {formatearMoneda(calcularGanancia)} ({calcularMargenGanancia.toFixed(1)}%)
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Ganancia y Margen
                          </Typography>
                        </Paper>
                      </Grid>
                    </Grid>
                  </Box>

                  {/* Alertas de rentabilidad */}
                  {calcularMargenGanancia < 15 && (
                    <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        ?? Margen de ganancia bajo ({calcularMargenGanancia.toFixed(1)}%)
                      </Typography>
                      <Typography variant="caption">
                        Considera aumentar el precio de venta o reducir costos para mejorar la rentabilidad.
                      </Typography>
                    </Alert>
                  )}

                  {calcularGanancia < 0 && (
                    <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        ? Pérdida por unidad ({formatearMoneda(Math.abs(calcularGanancia))})
                      </Typography>
                      <Typography variant="caption">
                        El precio de venta es menor que el costo. Ajusta los precios urgentemente.
                      </Typography>
                    </Alert>
                  )}

                  {/* Botones de acción */}
                  <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, pt: 2 }}>
                    <Button
                      variant="outlined"
                      onClick={handleCloseModal}
                      startIcon={<CloseIcon />}
                      sx={{
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 600,
                        px: 3,
                      }}
                    >
                      Cancelar
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleSaveChanges}
                      startIcon={<SaveIcon />}
                      sx={{
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 600,
                        backgroundColor: "#5b5b5f",
                        px: 3,
                        "&:hover": {
                          backgroundColor: alpha("#5b5b5f", 0.9),
                        },
                      }}
                    >
                      Guardar Cambios
                    </Button>
                  </Box>
                </>
              )}
            </Box>
          </Box>
        </Modal>

        {/* Modal para mostrar vendedores del paquete */}
        <Modal
          open={modalVendedores.open}
          onClose={handleCloseModalVendedores}
          aria-labelledby="modal-vendedores-titulo"
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: { xs: "95%", md: "60%", lg: "50%" },
              maxWidth: 700,
              maxHeight: "85vh",
              bgcolor: "background.paper",
              boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
              borderRadius: 3,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                p: 3,
                backgroundColor: theme.palette.info.main,
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    bgcolor: "rgba(255,255,255,0.2)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <PersonIcon sx={{ fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Servicios
                  </Typography>
                </Box>
              </Box>
              <IconButton
                onClick={handleCloseModalVendedores}
                sx={{
                  color: "white",
                  bgcolor: "rgba(255,255,255,0.1)",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            <Box sx={{ p: 3, maxHeight: "calc(85vh - 140px)", overflowY: "auto" }}>
              {modalVendedores.loading ? (
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", py: 6, gap: 2 }}>
                  <CircularProgress size={40} />
                  <Typography variant="body2" color="text.secondary">
                    Cargando servicios…
                  </Typography>
                </Box>
              ) : modalVendedores.vendedores.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <PersonIcon sx={{ fontSize: 48, color: "#ccc", mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    No hay servicios con proveedor asociados a este paquete
                  </Typography>
                </Box>
              ) : (
                <TableContainer
                  component={Paper}
                  sx={{
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: alpha(theme.palette.info.main, 0.1) }}>
                        <TableCell sx={{ fontWeight: 600 }}>Servicio</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Proveedor</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {modalVendedores.vendedores.map((vendedor) => (
                        <TableRow key={vendedor._key}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {vendedor.nombreServicio}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {vendedor.nombreProveedor}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
                <Button
                  variant="contained"
                  onClick={handleCloseModalVendedores}
                  startIcon={<CloseIcon />}
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 600,
                    backgroundColor: theme.palette.info.main,
                    px: 3,
                    "&:hover": { backgroundColor: alpha(theme.palette.info.main, 0.9) },
                  }}
                >
                  Cerrar
                </Button>
              </Box>
            </Box>
          </Box>
        </Modal>

        {/* Modal para mostrar servicios del grupo */}
        <Modal
          open={modalServicios.open}
          onClose={handleCloseModalServicios}
          aria-labelledby="modal-servicios-titulo"
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: { xs: "95%", md: "70%", lg: "60%" },
              maxWidth: 800,
              maxHeight: "85vh",
              bgcolor: "background.paper",
              boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
              borderRadius: 3,
              overflow: "hidden",
            }}
          >
            {/* Header del modal */}
            <Box
              sx={{
                p: 3,
                backgroundColor: theme.palette.warning.main,
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    bgcolor: "rgba(255,255,255,0.2)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <BallotIcon sx={{ fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Servicios del grupo
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    {modalServicios.receta?.descripcionProductoTerminado}
                  </Typography>
                </Box>
              </Box>
              <IconButton
                onClick={handleCloseModalServicios}
                sx={{
                  color: "white",
                  bgcolor: "rgba(255,255,255,0.1)",
                  "&:hover": {
                    bgcolor: "rgba(255,255,255,0.2)",
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Contenido del modal */}
            <Box sx={{ p: 3, maxHeight: "calc(85vh - 140px)", overflowY: "auto" }}>
              {modalServicios.servicios.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <BallotIcon sx={{ fontSize: 48, color: "#ccc", mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    No hay servicios asociados a este grupo
                  </Typography>
                </Box>
              ) : (
                <TableContainer
                  component={Paper}
                  sx={{
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: alpha(theme.palette.warning.main, 0.1) }}>
                        <TableCell sx={{ fontWeight: 600 }}>Servicio</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Proveedor</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {modalServicios.servicios.map((servicio, index) => (
                        <TableRow
                          key={servicio.idMateriaPrima}
                          sx={{
                            "&:hover": {
                              backgroundColor: alpha(theme.palette.warning.main, 0.02),
                            },
                            "&:nth-of-type(even)": {
                              backgroundColor: alpha(theme.palette.grey[50], 0.5),
                            },
                          }}
                        >
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <BallotIcon fontSize="small" sx={{ color: theme.palette.warning.main }} />
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {servicio.materiaPrima?.descripcion || "Sin descripción"}
                                </Typography>
                                {generarAtributosSubtexto(servicio.materiaPrima) && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                      display: "block",
                                      mt: 0.5,
                                      fontStyle: "italic",
                                      opacity: 0.7,
                                    }}
                                  >
                                    {generarAtributosSubtexto(servicio.materiaPrima)}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: "monospace",
                                fontWeight: 500,
                                bgcolor: alpha(theme.palette.warning.main, 0.1),
                                px: 1,
                                py: 0.5,
                                borderRadius: 1,
                                display: "inline-block",
                              }}
                            >
                              {[servicio?.entidad?.descripcion, servicio?.entidad?.apellido].filter(Boolean).join(" ").trim() || (servicio?.idEntidad ? `Proveedor ${servicio.idEntidad}` : "-")}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {/* Botón de cerrar */}
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
                <Button
                  variant="contained"
                  onClick={handleCloseModalServicios}
                  startIcon={<CloseIcon />}
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 600,
                    backgroundColor: theme.palette.warning.main,
                    px: 3,
                    "&:hover": {
                      backgroundColor: alpha(theme.palette.warning.main, 0.9),
                    },
                  }}
                >
                  Cerrar
                </Button>
              </Box>
            </Box>
          </Box>
        </Modal>
        
           {/* Modal para mostrar pasajeros confirmados (lazy) */}
           <Modal
          open={modalPasajerosConfirmados.open}
          onClose={handleCloseModalPasajerosConfirmados}
          aria-labelledby="modal-pasajeros-confirmados-titulo"
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: { xs: "95%", md: "60%", lg: "50%" },
              maxWidth: 720,
              maxHeight: "85vh",
              bgcolor: "background.paper",
              boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
              borderRadius: 3,
              overflow: "hidden",
            }}
          >
            {/* Header del modal */}
            <Box
              sx={{
                p: 3,
                backgroundColor: theme.palette.info.main,
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    bgcolor: "rgba(255,255,255,0.2)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <GroupsIcon sx={{ fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Pasajeros Confirmados
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    {modalPasajerosConfirmados.receta?.descripcionProductoTerminado || "Sin grupo"}{" "}
                    {modalPasajerosConfirmados.receta?.destino
                      ? ` - ${modalPasajerosConfirmados.receta.destino}`
                      : ""}
                  </Typography>
                </Box>
              </Box>
              <IconButton
                onClick={handleCloseModalPasajerosConfirmados}
                sx={{
                  color: "white",
                  bgcolor: "rgba(255,255,255,0.1)",
                  "&:hover": {
                    bgcolor: "rgba(255,255,255,0.2)",
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Contenido del modal */}
            <Box sx={{ p: 3, maxHeight: "calc(85vh - 140px)", overflowY: "auto" }}>
              {modalPasajerosConfirmados.loading ? (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <CircularProgress />
                  <Typography variant="body2" sx={{ mt: 2 }} color="text.secondary">
                    Cargando pasajeros...
                  </Typography>
                </Box>
              ) : modalPasajerosConfirmados.error ? (
                <Alert severity="error">{modalPasajerosConfirmados.error}</Alert>
              ) : modalPasajerosConfirmados.pasajeros.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <GroupsIcon sx={{ fontSize: 48, color: "#ccc", mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    No hay pasajeros confirmados
                  </Typography>
                </Box>
              ) : (
                <TableContainer
                  component={Paper}
                  sx={{
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: alpha(theme.palette.info.main, 0.1) }}>
                        <TableCell sx={{ fontWeight: 600, width: 40 }}>#</TableCell>
                        <TableCell sx={{ fontWeight: 600, textAlign: "left !important" }}>Apellido</TableCell>
                        <TableCell sx={{ fontWeight: 600, textAlign: "left !important" }}>Nombre</TableCell>
                        <TableCell sx={{ fontWeight: 600, textAlign: "left" }}>DNI</TableCell>
                        <TableCell sx={{ fontWeight: 600, textAlign: "left" }}>Observaciones</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {modalPasajerosConfirmados.pasajeros.map((pasajero, index) => {
                        const { nombre, apellido } = resolvePersonaParts(pasajero)
                        const dni = pasajero.dniCuitCuil || pasajero.dni || "-"

                        return (
                          <TableRow
                            key={`${pasajero.idEntidad || "pasajero"}-${index}`}
                            sx={{
                              "&:hover": {
                                backgroundColor: alpha(theme.palette.info.main, 0.02),
                              },
                              "&:nth-of-type(even)": {
                                backgroundColor: alpha(theme.palette.grey[50], 0.5),
                              },
                            }}
                          >
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {index + 1}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ textAlign: "left !important" }}>
                              <Typography variant="body2" sx={{ fontWeight: 500, textAlign: "left", width: "100%" }}>
                                {apellido || "-"}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ textAlign: "left !important" }}>
                              <Typography variant="body2" sx={{ fontWeight: 500, textAlign: "left", width: "100%" }}>
                                {nombre || "-"}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ textAlign: "left" }}>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {dni}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ textAlign: "left" }}>
                              <Typography variant="body2" color="text.secondary">
                                {pasajero.observaciones || "-"}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {/* Botón de cerrar */}
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
                <Button
                  variant="contained"
                  onClick={handleCloseModalPasajerosConfirmados}
                  startIcon={<CloseIcon />}
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 600,
                    backgroundColor: theme.palette.info.main,
                    px: 3,
                    "&:hover": {
                      backgroundColor: alpha(theme.palette.info.main, 0.9),
                    },
                  }}
                >
                  Cerrar
                </Button>
              </Box>
            </Box>
          </Box>
        </Modal>

        {/* Modal de cuponera por pasajeros */}
        <Modal
          open={modalCuponera.open}
          onClose={modalCuponera.procesando ? undefined : handleCloseModalCuponera}
          aria-labelledby="modal-cuponera-titulo"
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: { xs: "96%", md: "75%", lg: "65%" },
              maxWidth: 980,
              maxHeight: "88vh",
              bgcolor: "background.paper",
              boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
              borderRadius: 3,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                p: 3,
                backgroundColor: theme.palette.info.main,
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    bgcolor: "rgba(255,255,255,0.2)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <PrintIcon sx={{ fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Imprimir cuponera
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Seleccione pasajeros para generar la cuponera
                  </Typography>
                </Box>
              </Box>
              <IconButton
                onClick={handleCloseModalCuponera}
                disabled={modalCuponera.procesando}
                sx={{
                  color: "white",
                  bgcolor: "rgba(255,255,255,0.1)",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
                  "&.Mui-disabled": { color: "rgba(255,255,255,0.6)" },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            <Box sx={{ p: 3, maxHeight: "calc(88vh - 140px)", overflowY: "auto" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, mb: 2, flexWrap: "wrap" }}>
                <Typography variant="body2" color="text.secondary">
                  Pasajeros seleccionados: {modalCuponera.selectedKeys.length} / {modalCuponera.pasajeros.length}
                </Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleSeleccionarTodosCuponera}
                    disabled={modalCuponera.loading || modalCuponera.procesando || modalCuponera.pasajeros.length === 0}
                  >
                    Seleccionar todos
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleLimpiarSeleccionCuponera}
                    disabled={modalCuponera.loading || modalCuponera.procesando || modalCuponera.selectedKeys.length === 0}
                  >
                    Limpiar
                  </Button>
                </Box>
              </Box>

              {modalCuponera.loading ? (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <CircularProgress />
                  <Typography variant="body2" sx={{ mt: 2 }} color="text.secondary">
                    Cargando pasajeros...
                  </Typography>
                </Box>
              ) : modalCuponera.error ? (
                <Alert severity="error">{modalCuponera.error}</Alert>
              ) : modalCuponera.pasajeros.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <GroupsIcon sx={{ fontSize: 48, color: "#ccc", mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    No hay pasajeros disponibles para cuponera
                  </Typography>
                </Box>
              ) : (
                <TableContainer
                  component={Paper}
                  sx={{
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: alpha(theme.palette.info.main, 0.1) }}>
                        <TableCell sx={{ fontWeight: 600, width: 48 }}>#</TableCell>
                        <TableCell
                          align="left"
                          sx={{
                            fontWeight: 600,
                            textAlign: "left !important",
                            paddingLeft: `${theme.spacing(2)} !important`,
                          }}
                        >
                          Grupo
                        </TableCell>
                        <TableCell
                          align="left"
                          sx={{
                            fontWeight: 600,
                            textAlign: "left !important",
                            paddingLeft: `${theme.spacing(2)} !important`,
                          }}
                        >
                          Destino
                        </TableCell>
                        <TableCell
                          align="left"
                          sx={{
                            fontWeight: 600,
                            textAlign: "left !important",
                            paddingLeft: `${theme.spacing(2)} !important`,
                          }}
                        >
                          Nombre
                        </TableCell>
                        <TableCell
                          align="left"
                          sx={{
                            fontWeight: 600,
                            textAlign: "left !important",
                            paddingLeft: `${theme.spacing(2)} !important`,
                          }}
                        >
                          Apellido
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, textAlign: "left" }}>DNI</TableCell>
                        <TableCell sx={{ fontWeight: 600, textAlign: "center", width: 70 }}>Sel.</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {modalCuponera.pasajeros.map((pasajero, index) => {
                        const checked = modalCuponera.selectedKeys.includes(pasajero.key)
                        return (
                          <TableRow
                            key={pasajero.key}
                            sx={{
                              "&:hover": { backgroundColor: alpha(theme.palette.info.main, 0.02) },
                              "&:nth-of-type(even)": { backgroundColor: alpha(theme.palette.grey[50], 0.5) },
                            }}
                          >
                            <TableCell>{index + 1}</TableCell>
                            <TableCell
                              align="left"
                              sx={{
                                textAlign: "left !important",
                                paddingLeft: `${theme.spacing(2)} !important`,
                              }}
                            >
                              {pasajero.grupo || "-"}
                            </TableCell>
                            <TableCell
                              align="left"
                              sx={{
                                textAlign: "left !important",
                                paddingLeft: `${theme.spacing(2)} !important`,
                              }}
                            >
                              {pasajero.destino || "-"}
                            </TableCell>
                            <TableCell
                              align="left"
                              sx={{
                                textAlign: "left !important",
                                paddingLeft: `${theme.spacing(2)} !important`,
                              }}
                            >
                              {pasajero.nombre || "-"}
                            </TableCell>
                            <TableCell
                              align="left"
                              sx={{
                                textAlign: "left !important",
                                paddingLeft: `${theme.spacing(2)} !important`,
                              }}
                            >
                              {pasajero.apellido || "-"}
                            </TableCell>
                            <TableCell sx={{ textAlign: "left" }}>{pasajero.dniCuitCuil || "-"}</TableCell>
                            <TableCell sx={{ textAlign: "center" }}>
                              <Checkbox
                                checked={checked}
                                onChange={() => handleTogglePasajeroCuponera(pasajero.key)}
                                disabled={modalCuponera.procesando}
                                size="small"
                              />
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3, gap: 1 }}>
                <Button
                  variant="outlined"
                  onClick={handleCloseModalCuponera}
                  disabled={modalCuponera.procesando}
                >
                  Cerrar
                </Button>
                <Button
                  variant="contained"
                  onClick={handleImprimirCuponeraSeleccionados}
                  disabled={
                    modalCuponera.loading ||
                    modalCuponera.procesando ||
                    modalCuponera.selectedKeys.length === 0
                  }
                  startIcon={modalCuponera.procesando ? <CircularProgress size={16} color="inherit" /> : <PrintIcon />}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    backgroundColor: theme.palette.info.main,
                    "&:hover": { backgroundColor: alpha(theme.palette.info.main, 0.9) },
                  }}
                >
                  {modalCuponera.procesando ? "Generando..." : "Imprimir cuponera"}
                </Button>
              </Box>
            </Box>
          </Box>
        </Modal>

        {/* Modal para editar paquete - GRANDE */}
        <Modal
          open={modalEdicion.open}
          onClose={() => handleCloseModalEdicion()}
          aria-labelledby="modal-edicion-titulo"
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "95vw",
              height: "auto",
              maxHeight: "90vh",
              bgcolor: "background.paper",
              boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
              borderRadius: 3,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Header del modal */}
            <Box
              sx={{
                p: 1,
                borderBottom: "1px solid",
                borderColor: "divider",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                bgcolor: alpha(theme.palette.primary.main, 0.05),
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1, minWidth: 0 }}>
                <BallotIcon sx={{ color: theme.palette.primary.main, fontSize: 20, flexShrink: 0 }} />
                <Typography variant="body1" sx={{ fontWeight: 600, fontSize: "0.875rem", flexShrink: 0 }}>
                    Editar Grupo
                  </Typography>
                {modalEdicion.paquete && (() => {
                  // Buscar el item completo en itemTresLetras para obtener los atributos
                  const itemCompleto = itemTresLetras?.find(it => it.id === modalEdicion.paquete.idProductoTerminado)
                  
                  // Formatear atributos
                  let attributeValues = ""
                  if (itemCompleto && itemAtributoNoEliminados) {
                    const activeAttributes = itemAtributoNoEliminados.filter((attr) => !attr.eliminado)
                    attributeValues = activeAttributes
                      .map((attr, index) => {
                        const attributeValue = itemCompleto[`itemDatoAtributo${index + 1}`]
                        return attributeValue ? attributeValue : ""
                      })
                      .filter((val) => val.trim() !== "")
                      .join(" y ")
                  }
                  
                  // Construir el texto completo
                  const nombreGrupo = modalEdicion.paquete.descripcionProductoTerminado || "Sin descripción"
                  const atributosTexto = attributeValues ? ` - ${attributeValues}` : ""
                  const destinoTexto = modalEdicion.paquete.destino ? ` con destino a ${modalEdicion.paquete.destino}` : ""
                  
                  return (
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: "text.secondary",
                        ml: 1,
                        flex: 1,
                        minWidth: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {nombreGrupo}{atributosTexto}{destinoTexto}
                    </Typography>
                  )
                })()}
                </Box>
              <IconButton onClick={() => handleCloseModalEdicion()} size="small" sx={{ flexShrink: 0 }}>
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Contenido del modal */}
            <Box
              sx={{
                flex: 1,
                overflow: "auto",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {modalEdicion.paquete && (
                <RecetaComponent
                  modoEdicion={true}
                  paqueteInicial={modalEdicion.paquete}
                  mostrarAnaliticos={true}
                  enModal={true}
                  onGuardadoExitoso={handleRecetaGuardada}
                  onCancelar={handleCloseModalEdicion}
                  ocultarCantidadYCostoXCantidadInicial={modalEdicion.ocultarCantidadYCostoXCantidad}
                />
              )}
            </Box>
          </Box>
        </Modal>

        {/* Modal para editar pasajeros - GRANDE */}
        <Modal
          open={openModalPasajeros}
          onClose={handleCloseModalPasajeros}
          aria-labelledby="modal-pasajeros-titulo"
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "95vw",
              height: "90vh",
              bgcolor: "background.paper",
              boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
              borderRadius: 3,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Header del modal */}
            <Box
              sx={{
                p: 1,
                borderBottom: "1px solid",
                borderColor: "divider",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                bgcolor: alpha(theme.palette.primary.main, 0.05),
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <GroupsIcon sx={{ color: theme.palette.primary.main, fontSize: 24 }} />
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "1.1rem" }}>
                    {recetaPasajeros 
                      ? `Gestionar Pasajeros - ${recetaPasajeros.grupoFormateado?.label || "Sin grupo"} con destino a ${recetaPasajeros.destinoFormateado?.label || recetaPasajeros.destino || "Sin destino"}`
                      : "Gestionar Pasajeros del Paquete"
                    }
                  </Typography>
                </Box>
              </Box>
              <IconButton onClick={handleCloseModalPasajeros} size="small">
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Contenido del modal */}
            <Box
              sx={{
                flex: 1,
                overflow: "auto",
                p: 1,
              }}
            >
              {recetaPasajeros && recetaPasajeros.grupoFormateado && recetaPasajeros.destinoFormateado && (
                <GestionGenerarClientesPaquete
                  ref={gestionPasajerosRef}
                  grupoInicial={recetaPasajeros.grupoFormateado}
                  destinoInicial={recetaPasajeros.destinoFormateado}
                  modoModal={true}
                  soloLectura={Boolean(recetaPasajeros?.cerrado)}
                  onClose={handleCloseModalPasajeros}
                />
              )}
            </Box>
          </Box>
        </Modal>

        {/* Modal para crear paquete - GRANDE */}
        <Modal
          open={openCrearPaqueteModal}
          onClose={handleCloseCrearPaqueteModal}
          aria-labelledby="modal-crear-paquete-titulo"
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "90vw",
              height: "90vh",
              bgcolor: "background.paper",
              boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
              borderRadius: 3,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Header del modal */}
            <Box
              sx={{
                p: 2,
                borderBottom: "1px solid",
                borderColor: "divider",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                bgcolor: alpha(theme.palette.primary.main, 0.05),
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1, minWidth: 0 }}>
                <BallotIcon sx={{ color: theme.palette.primary.main, fontSize: 28, flexShrink: 0 }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "1.1rem", flexShrink: 0 }}>
                    Crear Grupo
                  </Typography>
                  {grupoSeleccionadoCrear && (() => {
                    // Buscar el item completo en itemTresLetras para obtener los atributos
                    const itemCompleto = itemTresLetras?.find(it => it.id === grupoSeleccionadoCrear.idItem)
                    
                    // Formatear atributos
                    let attributeValues = ""
                    if (itemCompleto && itemAtributoNoEliminados) {
                      const activeAttributes = itemAtributoNoEliminados.filter((attr) => !attr.eliminado)
                      attributeValues = activeAttributes
                        .map((attr, index) => {
                          const attributeValue = itemCompleto[`itemDatoAtributo${index + 1}`]
                          return attributeValue ? attributeValue : ""
                        })
                        .filter((val) => val.trim() !== "")
                        .join(" y ")
                    }
                    
                    // Construir el texto completo
                    const nombreGrupo = grupoSeleccionadoCrear.descripcion || "Sin descripción"
                    const atributosTexto = attributeValues ? ` - ${attributeValues}` : ""
                    
                    return (
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: "rgb(255, 193, 7)", // ? Color amarillo RGB
                          fontWeight: 500,
                          mt: 0.5,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {nombreGrupo}{atributosTexto}
                      </Typography>
                    )
                  })()}
                </Box>
              </Box>
              <IconButton onClick={handleCloseCrearPaqueteModal} size="small">
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Contenido del modal */}
            <Box
              sx={{
                flex: 1,
                overflow: "auto",
              }}
            >
              {crearPaqueteModalConfig.resolved ? (
                <RecetaComponent
                  key={`crear-paquete-${crearPaqueteModalConfig.nochesCalculoAutomatico ? "auto" : "manual"}`}
                  modoEdicion={false}
                  mostrarAnaliticos={true}
                  enModal={true}
                  onGuardadoExitoso={handlePaqueteCreado}
                  onCancelar={handleCloseCrearPaqueteModal}
                  ocultarCantidadYCostoXCantidadInicial={paquetesOcultarCantidadFlag}
                  nochesCalculoAutomaticoInicial={crearPaqueteModalConfig.nochesCalculoAutomatico}
                  onBaseItemChange={setGrupoSeleccionadoCrear} // ? Callback para actualizar el grupo seleccionado
                />
              ) : (
                <Box
                  sx={{
                    minHeight: 240,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CircularProgress size={28} />
                </Box>
              )}
            </Box>
          </Box>
        </Modal>
      </Box>
    </ThemeProvider>
  )
}

export default TablaReceta






